import React, { useState, useRef, useEffect, useCallback, memo, useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  ButtonGroup,
  IconButton,
  Badge,
  Card,
  CardBody,
  Heading,
  Divider,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  NumberInput,
  NumberInputField,
  Switch,
  Tooltip,
  Checkbox,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverBody,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Spinner,
  Image,
  Spacer,
  SimpleGrid,
  Center,
  Stack,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';

import { ChevronLeftIcon, ChevronRightIcon, EditIcon, CheckIcon, CloseIcon, CalendarIcon, AddIcon, TimeIcon, WarningIcon, SmallCloseIcon, PlusSquareIcon } from '@chakra-ui/icons';
import { BiCalculator } from 'react-icons/bi';
import supabase from '../supabaseClient';
import { getClientDisplayAge } from '../utils/clientAge';
import { compareNamesByFamilyName, formatNameLastCommaFirst } from '../utils/clientNameDisplay';
import {
  formatAttendanceStatusLabel,
  getAttendanceStatusColorScheme,
  formatMakeupBadgeDate,
  isCancelledOrLateCancellationStatus,
} from '../utils/attendanceDisplay';
import {
  findMatchingClientService,
  getReservationFeeDeduction,
  grossToNetAmountPaid,
  netToGrossAmountPaid,
  shouldApplyReservationFee,
  getBillingRateDate,
  getClientBillingRate,
  isSundayDate,
  resolveBillingServiceType,
} from '../utils/reservationFeeUtils';
import { getValidServiceFee, getSessionServiceFeeAsync } from '../utils/serviceFeeUtils';
import {
  calculateReceiptSplit,
  getCenterVatBreakdown,
  shouldUseSplitReceipts,
  getSameDayReceiptMode,
  buildCombinedSplitReceiptSummary,
  buildCombinedCenterReceiptSummary,
  isPromoReceiptSession,
  isOngoingPromoSession,
  resolvePromoSplitReceiptContext,
} from '../utils/receiptSplitUtils';
import { upsertInitialEvaluationFromAttendance } from '../utils/initialEvaluationUtils';
import {
  canApplyGraceCancellation,
  countGraceCancellationsForYear,
  getGraceCancellationHelperText,
  GRACE_CANCELLATIONS_PER_YEAR,
} from '../utils/graceCancellationUtils';
import { isSupposedMakeupSession } from '../utils/supposedMakeupUtils';
import SessionTrendsChart from './SessionTrendsChart';
import PendingMakeupByClientView from './PendingMakeupByClientView';
import NewClientRemindersView from './NewClientRemindersView';
import MakeupOpportunitiesView from './MakeupOpportunitiesView';
import {
  buildAttendanceMap,
  buildAvailableTherapistSlots,
  buildMakeupOpportunities,
  filterFutureSlotsForToday,
  isPendingMakeupRecord,
  sortMakeupAvailabilitySlots,
} from '../utils/makeupOpportunityUtils';

const BASE_DAILY_SCHEDULE_VIEWS = ['clients', 'new-client-reminders', 'therapists', 'unpaid', 'pending-makeup', 'makeup-opportunities'];
const SESSION_TRENDS_VIEW = 'session-trends';
const DAILY_TIME_SLOTS = Array.from({ length: 11 }, (_, i) => `${i + 8}:00`);

// Helper function to get date string in local timezone (YYYY-MM-DD format)
const getLocalDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getTomorrowLocalDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow;
};

async function getPromoAwareReceiptSplit({
  selectedSession,
  matchingService,
  attendanceForm,
  sessionDateStr,
  totalDue,
  therapistFee,
  billingServiceType,
}) {
  const isPromo = isPromoReceiptSession({
    session: selectedSession,
    matchingService,
    isCurrentSession: true,
    isPromoFormValue: attendanceForm.isPromo,
  });

  let promoCycleRows = [];
  if (isPromo) {
    const { data } = await supabase
      .from('session_attendance')
      .select('id, attendance_date, promo_session, amount_paid, status, billing_service_type')
      .eq('client_id', selectedSession.client.id)
      .not('promo_session', 'is', null)
      .eq('status', 'present')
      .order('attendance_date', { ascending: true });

    promoCycleRows = (data || []).filter(
      (row) => !row.billing_service_type || row.billing_service_type === billingServiceType
    );
  }

  const promoContext = resolvePromoSplitReceiptContext({
    session: selectedSession,
    matchingService,
    isPromoFormValue: attendanceForm.isPromo,
    promoCycleRows,
    sessionDate: sessionDateStr,
  });

  const splitTotalDue = promoContext.usePerSessionCenterReceipt
    ? (promoContext.packageTotal ?? totalDue)
    : totalDue;

  const split = calculateReceiptSplit({
    totalDue: splitTotalDue,
    therapistFee,
    isNewPromoPackage: promoContext.usePerSessionCenterReceipt,
  });

  return {
    ...split,
    therapistReceiptOnly: promoContext.therapistReceiptOnly,
    usePerSessionCenterReceipt: promoContext.usePerSessionCenterReceipt,
    displayTotalDue: promoContext.usePerSessionCenterReceipt
      ? (promoContext.packageTotal ?? totalDue)
      : totalDue,
    isOngoingPromoPackageSession:
      promoContext.usePerSessionCenterReceipt &&
      isOngoingPromoSession({
        session: selectedSession,
        matchingService,
        isPromoFormValue: attendanceForm.isPromo,
      }),
  };
}

const CenterVatBreakdown = ({
  centerVat,
  formatCurrency,
  amountDueLabel = 'Center Amount Due',
  showPwdDiscount = false,
}) => (
  <VStack align="start" spacing={2} width="100%">
    <HStack justify="space-between" width="100%">
      <Text fontSize="sm">Total Sales (VAT Inclusive):</Text>
      <Text fontSize="sm">{formatCurrency(centerVat.totalSales)}</Text>
    </HStack>
    <HStack justify="space-between" width="100%">
      <Text fontSize="sm">Less: VAT:</Text>
      <Text fontSize="sm">{formatCurrency(centerVat.vatAmount)}</Text>
    </HStack>
    <HStack justify="space-between" width="100%">
      <Text fontSize="sm">Amount Net of VAT:</Text>
      <Text fontSize="sm">{formatCurrency(centerVat.preVatAmount)}</Text>
    </HStack>
    {showPwdDiscount && centerVat.pwdDiscount > 0 && (
      <HStack justify="space-between" width="100%">
        <Text fontSize="sm">Less: Discount (PWD 20%):</Text>
        <Text fontSize="sm">{formatCurrency(centerVat.pwdDiscount)}</Text>
      </HStack>
    )}
    <HStack justify="space-between" width="100%">
      <Text fontSize="sm">Add: VAT:</Text>
      <Text fontSize="sm">{formatCurrency(centerVat.vatAmount)}</Text>
    </HStack>
    <Divider />
    <HStack justify="space-between" width="100%">
      <Text fontSize="sm" fontWeight="bold">{amountDueLabel}:</Text>
      <Text fontSize="sm" fontWeight="bold">{formatCurrency(centerVat.amountDue)}</Text>
    </HStack>
  </VStack>
);

const CombinedSameDayReceiptGuide = ({ summary, formatCurrency, formatTime, therapistReceiptOnly = false }) => {
  if (!summary || summary.sessionCount < 2) {
    return null;
  }

  if (summary.mode === 'mixed') {
    return (
      <Alert status="warning" borderRadius="md">
        <AlertIcon />
        <Box>
          <Text fontWeight="bold">Combined receipt unavailable</Text>
          <Text fontSize="sm">
            This client has {summary.sessionCount} sessions today with a mix of receipt-issuing
            and center-issued therapists. Issue separate receipts per session.
          </Text>
        </Box>
      </Alert>
    );
  }

  if (summary.mode === 'split') {
    return (
      <VStack align="start" spacing={3} width="100%">
        <Badge colorScheme="purple">
          {therapistReceiptOnly ? 'Same-Day Therapist Receipts' : 'Combined Same-Day Receipts'}
        </Badge>
      <Text fontSize="sm" color="gray.600">
        {summary.sessionCount} sessions today for this client.{' '}
        {therapistReceiptOnly
          ? `Write a separate therapist receipt for each session below${summary.sameTherapist ? ' (one OR may cover both if same therapist)' : ''}. No center OR is needed.`
          : `Issue one center OR for all center fees below, plus ${summary.sameTherapist
            ? 'a separate therapist OR with both sessions'
            : 'a separate therapist OR for each session'}.`}
        {summary.excludedPromoCount > 0 && (
          <> {' '}({summary.excludedPromoCount} promo session{summary.excludedPromoCount > 1 ? 's' : ''} excluded)</>
        )}
      </Text>
        {!therapistReceiptOnly && (
          <Box width="100%" p={3} borderWidth={1} borderRadius="md" bg="purple.50">
            <Text fontWeight="bold" mb={2}>Total Collected Today</Text>
            <Text fontSize="lg" fontWeight="bold">{formatCurrency(summary.combinedTotalDue)}</Text>
          </Box>
        )}
        <Box width="100%" p={3} borderWidth={1} borderRadius="md">
          <Text fontWeight="bold" mb={2}>
            {summary.sameTherapist
              ? therapistReceiptOnly
                ? 'Therapist Receipt (both sessions, No VAT)'
                : 'Therapist Receipt (1 OR with both sessions, No VAT)'
              : 'Therapist Receipts (No VAT)'}
          </Text>
          <VStack align="stretch" spacing={2} width="100%">
            {summary.sessionLines.map((line) => (
              <HStack key={line.sessionId} justify="space-between" width="100%">
                <Text fontSize="sm">
                  {formatTime(line.time)} · {line.serviceType} · {line.therapistName}
                  {line.isCurrent ? ' (this session)' : ''}
                </Text>
                <Text fontSize="sm" fontWeight="medium">{formatCurrency(line.therapistFee)}</Text>
              </HStack>
            ))}
          </VStack>
          <Divider my={2} />
          <HStack justify="space-between" width="100%">
            <Text fontSize="sm" fontWeight="bold">
              {summary.sameTherapist ? 'Therapist Receipt Total' : 'Total Therapist Receipts'}
            </Text>
            <Text fontSize="sm" fontWeight="bold">{formatCurrency(summary.combinedTherapistFees)}</Text>
          </HStack>
        </Box>
        {!therapistReceiptOnly && (
          <Box width="100%" p={3} borderWidth={1} borderRadius="md" bg="blue.50">
            <Text fontWeight="bold" mb={2}>Combined Center Receipt (1 OR)</Text>
            <Text fontSize="lg" fontWeight="bold" mb={2}>{formatCurrency(summary.combinedCenterFee)}</Text>
            {summary.showCenterVat && summary.combinedCenterFee > 0 && (
              <CenterVatBreakdown centerVat={summary.centerVat} formatCurrency={formatCurrency} />
            )}
          </Box>
        )}
      </VStack>
    );
  }

  return (
    <VStack align="start" spacing={3} width="100%">
      <Badge colorScheme="blue">Combined Same-Day Receipt</Badge>
      <Text fontSize="sm" color="gray.600">
        {summary.sessionCount} sessions today for this client. Issue one center OR for the
        combined total below.
        {summary.excludedPromoCount > 0 && (
          <> {' '}({summary.excludedPromoCount} promo session{summary.excludedPromoCount > 1 ? 's' : ''} excluded)</>
        )}
      </Text>
      <Box width="100%" p={3} borderWidth={1} borderRadius="md">
        <Text fontWeight="bold" mb={2}>Sessions Included</Text>
        <VStack align="stretch" spacing={2} width="100%">
          {summary.sessionLines.map((line) => (
            <HStack key={line.sessionId} justify="space-between" width="100%">
              <Text fontSize="sm">
                {formatTime(line.time)} · {line.serviceType} · {line.therapistName}
                {line.isCurrent ? ' (this session)' : ''}
              </Text>
              <Text fontSize="sm">{formatCurrency(line.totalDue)}</Text>
            </HStack>
          ))}
        </VStack>
      </Box>
      <Box width="100%" p={3} borderWidth={1} borderRadius="md" bg="blue.50">
        <Text fontWeight="bold" mb={2}>Combined Center Receipt</Text>
        <Text fontSize="lg" fontWeight="bold" mb={2}>{formatCurrency(summary.combinedTotalDue)}</Text>
        {summary.showCenterVat && summary.combinedTotalDue > 0 && (
          <CenterVatBreakdown
            centerVat={summary.centerVat}
            formatCurrency={formatCurrency}
            amountDueLabel="Total Amount Due"
            showPwdDiscount={summary.isPwd}
          />
        )}
      </Box>
    </VStack>
  );
};

const SplitReceiptGuide = ({
  totalDue,
  therapistFee,
  isVatInclusive,
  formatCurrency,
  therapistReceiptOnly = false,
  isNewPromoPackage = false,
  isOngoingPromoPackageSession = false,
}) => {
  const split = calculateReceiptSplit({
    totalDue,
    therapistFee,
    isNewPromoPackage,
  });
  const centerVat = isVatInclusive ? getCenterVatBreakdown(split.centerFee) : null;

  if (therapistReceiptOnly) {
    return (
      <VStack align="start" spacing={3} width="100%">
        <Badge colorScheme="purple">Therapist Receipt</Badge>
        <Text fontSize="sm" color="gray.600">
          Write only the therapist receipt for this session.
        </Text>
        <Box width="100%" p={3} borderWidth={1} borderRadius="md">
          <Text fontWeight="bold" mb={1}>Therapist Receipt (No VAT)</Text>
          <Text fontSize="lg">{formatCurrency(split.therapistFee)}</Text>
        </Box>
      </VStack>
    );
  }

  return (
    <VStack align="start" spacing={3} width="100%">
      <Badge colorScheme="purple">Split Receipts</Badge>
      <Text fontSize="sm" color="gray.600">
        Write two receipts for this session. PWD discounts are already reflected in the total below.
        {isNewPromoPackage && isOngoingPromoPackageSession && (
          <> Center receipt uses this session&apos;s share of the prepaid package (package total ÷ 10, less therapist fee).</>
        )}
        {isNewPromoPackage && !isOngoingPromoPackageSession && (
          <> Center receipt is for this session only (package total ÷ 10, less therapist fee).</>
        )}
      </Text>
      <Box width="100%" p={3} borderWidth={1} borderRadius="md" bg="purple.50">
        <Text fontWeight="bold" mb={2}>
          {isOngoingPromoPackageSession ? 'Package Total (prepaid)' : 'Total Collected'}
        </Text>
        <Text fontSize="lg" fontWeight="bold">{formatCurrency(split.totalDue)}</Text>
      </Box>
      <Box width="100%" p={3} borderWidth={1} borderRadius="md">
        <Text fontWeight="bold" mb={1}>Therapist Receipt (No VAT)</Text>
        <Text fontSize="lg">{formatCurrency(split.therapistFee)}</Text>
      </Box>
      <Box width="100%" p={3} borderWidth={1} borderRadius="md">
        <Text fontWeight="bold" mb={2}>Center Receipt</Text>
        <Text fontSize="lg" fontWeight="bold" mb={2}>{formatCurrency(split.centerFee)}</Text>
        {centerVat && split.centerFee > 0 && (
          <CenterVatBreakdown centerVat={centerVat} formatCurrency={formatCurrency} />
        )}
      </Box>
    </VStack>
  );
};

const VATBreakdown = ({ selectedSession, matchingService, attendanceForm }) => {
  const [totalSales, setTotalSales] = useState(null);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount);
  };


  const getPackageBaseRate = async (serviceType) => {
    try {
      const currentDate = getLocalDateString(new Date());
      
      const { data, error } = await supabase
        .from('service_rates')
        .select('*')
        .eq('service_type', serviceType)
        .eq('session_type', 'Regular')
        .eq('is_package', true)
        .lte('valid_from', currentDate)
        .or(`valid_until.is.null,valid_until.gte.${currentDate}`)
        .single();
  
      if (error) throw error;
      return data?.base_rate || null;
    } catch (error) {
      console.error('Error fetching package rate:', error);
      return null;
    }
  };

  console.log('Type: ', selectedSession.session_type);

  useEffect(() => {
    const fetchAndSetRate = async () => {
      if (attendanceForm.isPromo) {
        const packageRate = await getPackageBaseRate(selectedSession.service_type);
        setTotalSales(packageRate || matchingService.base_rate * 10);
      } else if (selectedSession.session_type === 'IE') {
        setTotalSales(3500);
      }
        else {
        setTotalSales(matchingService.base_rate);
      }
    };

    fetchAndSetRate();
  }, [attendanceForm.isPromo, selectedSession.service_type, matchingService.base_rate]);

  if (totalSales === null) return <Spinner size="sm" />;

  const preVatAmount = totalSales / 1.12;
  const vatAmount = totalSales - preVatAmount;

  let pwdDiscount = 0;
  if (selectedSession.session_type !== 'IE') {
    pwdDiscount = selectedSession.client.pwd ? (preVatAmount * 0.2) : 0;
  }
  const finalAmount = (preVatAmount - pwdDiscount) + vatAmount;

  return (
    <VStack align="start" spacing={2}>
      <HStack justify="space-between" width="100%">
        <Text>Total Sales (VAT Inclusive):</Text>
        <Text>{formatCurrency(totalSales)}</Text>
      </HStack>
      <HStack justify="space-between" width="100%">
        <Text>Less: VAT:</Text>
        <Text>{formatCurrency(vatAmount)}</Text>
      </HStack>
      <HStack justify="space-between" width="100%">
        <Text>Amount Net of VAT:</Text>
        <Text>{formatCurrency(preVatAmount)}</Text>
      </HStack>
      {selectedSession.client.pwd && (
        <HStack justify="space-between" width="100%">
          <Text>Less: Discount (PWD 20%):</Text>
          <Text>{formatCurrency(pwdDiscount)}</Text>
        </HStack>
      )}
      <HStack justify="space-between" width="100%">
        <Text>Add: VAT:</Text>
        <Text>{formatCurrency(vatAmount)}</Text>
      </HStack>
      <Divider />
      <HStack justify="space-between" width="100%">
        <Text fontWeight="bold">Total Amount Due:</Text>
        <Text fontWeight="bold">{formatCurrency(finalAmount)}</Text>
      </HStack>
    </VStack>
  );
};

const TherapistSchedule = ({ therapistId, selectedDate }) => {
  const [sessions, setSessions] = useState([]);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isDeckConfirmed, setIsDeckConfirmed] = useState(false);
  const [signatureImage, setSignatureImage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const toast = useToast();
  const contextRef = useRef(null);

  const formatTime = (time) => {
    const hour = parseInt(time.split(':')[0]);
    const h = hour % 12 || 12;
    const ampm = hour < 12 ? 'AM' : 'PM';
    return `${h}:00 ${ampm}`;
  };

  useEffect(() => {
    if (isSignatureModalOpen && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      // Set white background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Set drawing style
      ctx.strokeStyle = 'black';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      console.log('Canvas initialized');
    }
  }, [isSignatureModalOpen]);

  let lastX = 0;
  let lastY = 0;

  const initializeCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    // Set initial canvas state
    context.fillStyle = 'white';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.strokeStyle = '#000';
    context.lineWidth = 2;
    context.lineCap = 'round';
    contextRef.current = context;
  };

  const startDrawing = (e) => {
    isDrawingRef.current = true;
    lastX = e.nativeEvent.offsetX;
    lastY = e.nativeEvent.offsetY;
    console.log('Start drawing', { lastX, lastY });
  };

  const draw = (e) => {
    if (!isDrawingRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    const currentX = e.nativeEvent.offsetX;
    const currentY = e.nativeEvent.offsetY;
    
    ctx.beginPath();
    ctx.moveTo(lastX, lastY);
    ctx.lineTo(currentX, currentY);
    ctx.stroke();
    
    lastX = currentX;
    lastY = currentY;
    
    console.log('Drawing', { currentX, currentY });
  };

  const stopDrawing = () => {
    isDrawingRef.current = false;
    console.log('Stop drawing');
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 2;
    
    console.log('Signature cleared');
  };

  useEffect(() => {
    fetchSessions();
    checkConfirmation();
    checkDeckConfirmation();
  }, [therapistId, selectedDate]);

  useEffect(() => {
    if (isSignatureModalOpen && canvasRef.current) {
      // Small delay to ensure modal is rendered
      const timer = setTimeout(() => {
        initializeCanvas();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isSignatureModalOpen]);

  const fetchSessions = async () => {
    const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    try {
      const { data } = await supabase
        .from('sessions')
        .select(`
          *,
          client:clients (*),
          session_attendance:session_attendance(*)
        `)
        .eq('therapist_id', therapistId)
        .eq('day', DAYS[selectedDate.getDay()])
        .lte('valid_from', getLocalDateString(selectedDate))
        .or(`valid_until.is.null,valid_until.gte.${getLocalDateString(selectedDate)}`);

      setSessions(data?.map(session => ({
        ...session,
        attendance: session.session_attendance.find(
          a => a.attendance_date === getLocalDateString(selectedDate)
        )
      })).sort((a, b) => parseInt(a.time) - parseInt(b.time)) || []);
    } catch (error) {
      console.error('Error:', error);
      toast({ title: 'Error fetching sessions', status: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const checkDeckConfirmation = async () => {
    const selectedDateStr = getLocalDateString(selectedDate);
    try {
      const { data, error } = await supabase
        .from('deck_confirmations')
        .select('*')
        .eq('therapist_id', therapistId)
        .eq('date', selectedDateStr)
        .single();

      console.log('DATA: ', data);
      if (error && error.code !== 'PGNF') throw error;
      setIsDeckConfirmed(!!data);
    } catch (error) {
      console.error('Error checking deck confirmation:', error);
    }
  };

  const checkConfirmation = async () => {
    const { data } = await supabase
      .from('attendance_confirmations')
      .select('*')
      .eq('therapist_id', therapistId)
      .eq('date', getLocalDateString(selectedDate));

    if (data && data.length > 0) {
      setIsConfirmed(true);
      setSignatureImage(data[0].signature);
    } else {
      setIsConfirmed(false);
      setSignatureImage(null);
    }
  };

  const handleConfirmDeck = async () => {
    try {
      const { error } = await supabase
        .from('deck_confirmations')
        .insert({
          therapist_id: therapistId,
          date: getLocalDateString(selectedDate),
          confirmed_at: new Date().toISOString()
        });

      if (error) throw error;

      setIsDeckConfirmed(true);
      toast({
        title: 'Deck confirmed',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error confirming deck:', error);
      toast({
        title: 'Error confirming deck',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleConfirm = () => {
    setIsSignatureModalOpen(true);
  };

  const handleSignatureConfirm = async () => {
    try {
      const canvas = canvasRef.current;
      const signatureData = canvas.toDataURL('image/png');
      
      const { error } = await supabase
        .from('attendance_confirmations')
        .insert({
          therapist_id: therapistId,
          date: getLocalDateString(selectedDate),
          confirmed_at: new Date().toISOString(),
          signature: signatureData
        });

      if (error) throw error;
      
      setIsSignatureModalOpen(false);
      await checkConfirmation(); // Refresh the data
      toast({ title: 'Attendance confirmed', status: 'success' });
    } catch (error) {
      toast({ title: 'Error confirming attendance', status: 'error' });
    }
  };

  const handleTouchStart = (e) => {
    e.preventDefault(); // Prevent scrolling when drawing
    if (e.touches && e.touches[0]) {
      const touch = e.touches[0];
      const rect = canvasRef.current.getBoundingClientRect();
      lastX = touch.clientX - rect.left;
      lastY = touch.clientY - rect.top;
      isDrawingRef.current = true;
      console.log('Touch start', { lastX, lastY });
    }
  };
  
  const handleTouchMove = (e) => {
    e.preventDefault(); // Prevent scrolling when drawing
    if (!isDrawingRef.current) return;
    
    if (e.touches && e.touches[0]) {
      const touch = e.touches[0];
      const rect = canvasRef.current.getBoundingClientRect();
      const currentX = touch.clientX - rect.left;
      const currentY = touch.clientY - rect.top;
      
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      ctx.beginPath();
      ctx.moveTo(lastX, lastY);
      ctx.lineTo(currentX, currentY);
      ctx.stroke();
      
      lastX = currentX;
      lastY = currentY;
      
      console.log('Touch move', { currentX, currentY });
    }
  };

  const getSessionSummary = () => {
    const presentSessions = sessions.filter(s => s.attendance?.status === 'present');
    const presentTypes = presentSessions.reduce((acc, session) => {
      const type = session.attendance.session_type;
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});
  
    const typeBreakdown = Object.entries(presentTypes)
      .map(([type, count]) => `${count} ${type}`)
      .join(', ');
  
    return {
      total: sessions.length,
      present: presentSessions.length,
      absent: sessions.filter(s => s.attendance?.status === 'absent').length,
      cancelled: sessions.filter(s => s.attendance?.status === 'cancelled').length,
      presentTypeBreakdown: typeBreakdown
    };
  };

  const summary = getSessionSummary();

  return (
    <Box w="full">
      <Card mb={[3, 4, 6]} boxShadow="md" borderWidth="1px" borderColor="gray.200">
        <CardBody p={[3, 4, 6]}>
          <Stack
            direction={['column', 'column', 'row']}
            justify="space-between"
            align={['stretch', 'stretch', 'flex-start']}
            spacing={[4, 6, 8]}
          >
            {isDeckConfirmed && (
              <VStack align="start" spacing={[4, 5, 6]} flex="1">
                <HStack spacing={3}>
                  <CalendarIcon color="blue.500" boxSize={5} />
                  <Heading size={['sm', 'md']}>Daily Summary</Heading>
                </HStack>
                <SimpleGrid 
                  columns={[1, 2]} 
                  spacing={[3, 4, 6]} 
                  w="full"
                >
                  <Box bg="gray.50" p={[3, 4]} borderRadius="lg">
                    <VStack align="start" spacing={3}>
                      <HStack>
                        <PlusSquareIcon color="gray.600" boxSize={5} />
                        <Text fontWeight="medium">Total Sessions</Text>
                      </HStack>
                      <Text fontSize={["xl", "2xl"]} fontWeight="bold">{summary.total}</Text>
                    </VStack>
                  </Box>
                  <Box bg="green.50" p={[3, 4]} borderRadius="lg">
                    <VStack align="start" spacing={3}>
                      <HStack>
                        <CheckIcon color="green.600" boxSize={5} />
                        <Text fontWeight="medium">Present</Text>
                      </HStack>
                      <Text fontSize={["xl", "2xl"]} fontWeight="bold">{summary.present}</Text>
                      <Text fontSize="sm" color="gray.600">({summary.presentTypeBreakdown})</Text>
                    </VStack>
                  </Box>
                  <Box bg="red.50" p={[3, 4]} borderRadius="lg">
                    <VStack align="start" spacing={3}>
                      <HStack>
                        <CloseIcon color="red.600" boxSize={4} />
                        <Text fontWeight="medium">Late Cancellation</Text>
                      </HStack>
                      <Text fontSize={["xl", "2xl"]} fontWeight="bold">{summary.absent}</Text>
                    </VStack>
                  </Box>
                  <Box bg="orange.50" p={[3, 4]} borderRadius="lg">
                    <VStack align="start" spacing={3}>
                      <HStack>
                        <WarningIcon color="orange.600" boxSize={5} />
                        <Text fontWeight="medium">Cancelled</Text>
                      </HStack>
                      <Text fontSize={["xl", "2xl"]} fontWeight="bold">{summary.cancelled}</Text>
                    </VStack>
                  </Box>
                </SimpleGrid>
              </VStack>
            )}
            <VStack 
              justify="center" 
              align={['stretch', 'stretch', 'center']} 
              spacing={4} 
              minW={['auto', 'auto', '200px']}
            >
              {!isDeckConfirmed ? (
                <Button 
                  leftIcon={<CheckIcon />}
                  colorScheme="blue"
                  size={['md', 'lg']}
                  onClick={handleConfirmDeck}
                >
                  Confirm Deck
                </Button>
              ) : !isConfirmed && (
                <Button 
                  leftIcon={<CheckIcon />}
                  colorScheme="green"
                  size={['md', 'lg']}
                  onClick={handleConfirm}
                  isDisabled={
                    sessions.some(s => !s.attendance) ||
                    sessions.length === 0 ||
                    selectedDate > new Date()
                  }
                >
                  Confirm Attendance
                </Button>
              )}
              {signatureImage && (
                <Box p={3} borderWidth="1px" borderRadius="lg" bg="gray.50">
                  <VStack spacing={2}>
                    <Text fontSize="sm" color="gray.600" fontWeight="medium">
                      Signature Confirmed
                    </Text>
                    <Image
                      src={signatureImage}
                      alt="Therapist's signature"
                      maxW="150px"
                      maxH="60px"
                      objectFit="contain"
                    />
                  </VStack>
                </Box>
              )}
            </VStack>
          </Stack>
        </CardBody>
      </Card>

      <VStack align="stretch" spacing={[3, 4]}>
        {sessions.map(session => (
          <Card 
            key={session.id} 
            bg={
              session.attendance?.status === 'present' ? 'green.50' : 
              session.attendance?.status === 'absent' || session.attendance?.status === 'cancelled' ? 'red.50' : 
              'white'
            }
            borderWidth="1px"
            borderColor={
              session.attendance?.status === 'present' ? 'green.200' : 
              session.attendance?.status === 'absent' || session.attendance?.status === 'cancelled' ? 'red.200' : 
              'gray.200'
            }
            boxShadow="sm"
            _hover={{ boxShadow: 'md' }}
            transition="all 0.2s"
          >
            <CardBody p={[3, 4]}>
              <Stack
                direction={['column', 'row']}
                justify="space-between"
                align={['flex-start', 'center']}
                spacing={[3, 4, 6]}
              >
                <HStack spacing={[3, 4, 6]}>
                  <Center bg="gray.100" p={3} borderRadius="lg">
                    <VStack>
                      <TimeIcon boxSize={5} color="gray.600" />
                      <Text fontWeight="bold">{formatTime(session.time)}</Text>
                    </VStack>
                  </Center>
                  <VStack align="start" spacing={1}>
                    <Text fontSize={['md', 'lg']} fontWeight="semibold">
                      {session.client.name}
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      "{session.client.nickname}"
                    </Text>
                    <Badge 
                      mt={1}
                      colorScheme={
                        session.attendance?.status
                          ? getAttendanceStatusColorScheme(session.attendance.status)
                          : 'gray'
                      }
                    >
                      {session.attendance?.status
                        ? formatAttendanceStatusLabel(session.attendance.status)
                        : 'Pending'}
                    </Badge>
                    {session.attendance?.grace_cancellation && session.attendance?.status === 'absent' && (
                      <Badge mt={1} colorScheme="teal" variant="solid" size="sm">
                        Grace Cancellation
                      </Badge>
                    )}
                    {session.attendance?.waived_late_cancellation && session.attendance?.status === 'absent' && (
                      <Badge mt={1} colorScheme="cyan" variant="solid" size="sm">
                        Waived Late Cancellation
                      </Badge>
                    )}
                  </VStack>
                </HStack>
                {session.attendance?.notes && (
                  <Text 
                    color="gray.600" 
                    fontSize={['sm', 'md']}
                    flex="1"
                    maxW={['full', 'xl']}
                  >
                    {session.attendance.notes}
                  </Text>
                )}
              </Stack>
            </CardBody>
          </Card>
        ))}
      </VStack>

      <Modal 
        isOpen={isSignatureModalOpen} 
        onClose={() => setIsSignatureModalOpen(false)}
        isCentered
        size={['full', 'lg']}
      >
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent m={[4]} p={[3, 4]}>
          <ModalHeader>
            <Center>
              <HStack spacing={3}>
                <EditIcon boxSize={5} color="green.500" />
                <Text>Sign to Confirm Attendance</Text>
              </HStack>
            </Center>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4} textAlign="center" color="gray.600">
              Please sign below to confirm the attendance records for today.
            </Text>
            <Box 
              borderWidth={2} 
              borderRadius="lg" 
              overflow="hidden" 
              mb={4}
              boxShadow="inner"
            >
            <canvas
              ref={canvasRef}
              width={400}
              height={200}
              style={{ 
                width: '100%',
                height: '200px',
                backgroundColor: '#f8fafc',
                cursor: 'crosshair',
                border: '2px solid #2196F3',
                touchAction: 'none' // Add this line
              }}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={stopDrawing}
            />
            </Box>
            <Button
              variant="outline"
              onClick={clearSignature}
              w="full"
              leftIcon={<SmallCloseIcon />}
            >
              Clear Signature
            </Button>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsSignatureModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              colorScheme="green" 
              onClick={handleSignatureConfirm}
              leftIcon={<CheckIcon />}
            >
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

const DEFAULT_DOCUMENT_STATUS = {
  initial_document: false,
  given_ca: false,
  signed_ca: false,
  signed_cf: false,
  pwd_card: false,
  official_receipt: false,
};

const getDocumentStatusForClient = (client) => ({
  ...DEFAULT_DOCUMENT_STATUS,
  official_receipt: Boolean(client?.official_receipt),
});

// 1. First, move this outside the DailySchedule component, placing it just before it
const SessionCard = memo(({ 
  session,
  selectedDate,
  setSelectedSession,
  setAttendanceForm,
  setDocumentStatus,
  setIsAttendanceModalOpen,
  setPaidNoShowFee,
  setCancelledMakeupByTherapist,
  setRescheduleMakeup,
  setGraceCancellation,
  setWaivedLateCancellation,
  setSessionNotes,
  setIsNotesModalOpen,
  calculatePrice,
  formatCurrency,
  formatTime,
  onRefresh
  }) => {
    console.log(`🔄 SessionCard re-rendering for ${session.client.name}`);
    const [nextSession, setNextSession] = useState(null);
    const [hasB2BSessions, setHasB2BSessions] = useState(false);
    const [scheduledMakeupDate, setScheduledMakeupDate] = useState(null);
    const [collectableAmount, setCollectableAmount] = useState(null);
    const [makeupTimeDraft, setMakeupTimeDraft] = useState(session.time || '');
    const [updatingMakeupTime, setUpdatingMakeupTime] = useState(false);
    const toast = useToast();

    useEffect(() => {
      setMakeupTimeDraft(session.time || '');
    }, [session.id, session.time]);

    // Fetch scheduled makeup date if makeup_scheduled is true
    useEffect(() => {
      const fetchScheduledMakeupDate = async () => {
        if (session.attendance?.makeup_scheduled === true && session.attendance?.id) {
          try {
            // Find the makeup session that was created for this attendance
            const { data, error } = await supabase
              .from('sessions')
              .select('valid_from')
              .eq('session_attendance_id', session.attendance.id)
              .eq('is_makeup', true)
              .single();
            
            if (!error && data) {
              setScheduledMakeupDate(data.valid_from);
            }
          } catch (error) {
            console.error('Error fetching scheduled makeup date:', error);
          }
        }
      };
      
      fetchScheduledMakeupDate();
    }, [session.attendance?.makeup_scheduled, session.attendance?.id]);

    useEffect(() => {
      const computeCollectableAmount = async () => {
        const attendance = session.attendance;
        if (
          !attendance ||
          attendance.status !== 'present' ||
          attendance.is_paid
        ) {
          setCollectableAmount(null);
          return;
        }

        const sessionType = attendance.session_type || session.session_type;
        const grossAmount = attendance.amount_paid;

        if (sessionType !== 'IE' && sessionType !== 'Endorsement') {
          setCollectableAmount(grossAmount);
          return;
        }

        const selectedDateStr = getLocalDateString(selectedDate);
        const matchingService = findMatchingClientService(
          session.client?.client_services,
          session.therapist.specialization,
          selectedDateStr
        );

        const feeApplies = await shouldApplyReservationFee({
          clientId: session.client.id,
          sessionType,
          therapistId: session.therapist.id,
          therapistSpecialization: session.therapist.specialization,
          excludeAttendanceId: attendance.id,
        });
        const feeDeduction = getReservationFeeDeduction(matchingService, feeApplies);
        setCollectableAmount(grossToNetAmountPaid(grossAmount, feeDeduction));
      };

      computeCollectableAmount();
    }, [session, selectedDate]);

    // Add effect to check for B2B sessions
    useEffect(() => {
      const checkForB2BSessions = async () => {
        try {
          // Query for other sessions on the same day for this client
          const { data, error } = await supabase
            .from('sessions')
            .select('id, day, time')
            .eq('client_id', session.client.id)
            .eq('day', session.day)
            .neq('id', session.id) // Exclude current session
            .lte('valid_from', getLocalDateString(selectedDate))
            .or(`valid_until.is.null,valid_until.gte.${getLocalDateString(selectedDate)}`);
            
          if (error) throw error;
          
          // If we found any other sessions, set B2B to true
          setHasB2BSessions(data && data.length > 0);
          
          // Also add this property to the session object so it can be used in the JSX
          session.hasB2BSessions = data && data.length > 0;
        } catch (error) {
          console.error('Error checking for B2B sessions:', error);
        }
      };
      
      checkForB2BSessions();
    }, [session, selectedDate]);
  
    useEffect(() => {
      if (session.attendance) {
        fetchNextSession();
      }
    }, [session.attendance]);
  
    const fetchNextSession = useCallback(async () => {
      const currentDate = new Date();
      const currentDayIndex = currentDate.getDay();
      const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      
      try {
        const { data, error } = await supabase
          .from('sessions')
          .select(`
            day,
            time,
            therapist:therapists (
              name,
              specialization
            )
          `)
          .eq('client_id', session.client.id)
          .lte('valid_from', getLocalDateString(currentDate))
          .or(`valid_until.is.null,valid_until.gte.${getLocalDateString(currentDate)}`);
  
        if (error) throw error;
  
        if (data && data.length > 0) {
          const nextSessions = data.map(s => {
            const sessionDayIndex = daysOfWeek.indexOf(s.day);
            let daysUntil = sessionDayIndex - currentDayIndex;
            if (daysUntil <= 0) daysUntil += 7;
            
            const nextDate = new Date(currentDate);
            nextDate.setDate(currentDate.getDate() + daysUntil);
            
            return {
              ...s,
              nextDate
            };
          });
  
          const nextSession = nextSessions.sort((a, b) => a.nextDate - b.nextDate)[0];
          setNextSession(nextSession);
        }
      } catch (error) {
        console.error('Error fetching next session:', error);
      }
    }, [session.client.id]);
  
// Replace the handleAttendanceClick function in SessionCard with this updated version:

  const handleAttendanceClick = useCallback(async (options) => {
    const selectedDateStr = getLocalDateString(selectedDate);

    // IMPORTANT: Fetch fresh client services data to get the latest promo count
    const { data: freshClientServices, error: clientServicesError } = await supabase
      .from('client_services')
      .select(`
        client_id,
        service_type,
        base_rate,
        vat_inclusive,
        sunday_rate,
        sunday_regular_rate,
        reservation_fee,
        promo_count,
        pricing_valid_from,
        pricing_valid_until
      `)
      .eq('client_id', session.client.id)
      .lte('pricing_valid_from', selectedDateStr)
      .or(`pricing_valid_until.is.null,pricing_valid_until.gte.${selectedDateStr}`);

    if (clientServicesError) {
      console.error('Error fetching fresh client services:', clientServicesError);
      // Fall back to cached data if fresh fetch fails
    }

    // Use fresh data if available, otherwise fall back to cached data
    const clientServicesData = freshClientServices || session.client.client_services || [];
    const billingServiceType = resolveBillingServiceType(
      session,
      session.therapist,
      clientServicesData,
      selectedDateStr
    );

    const matchingService = findMatchingClientService(
      clientServicesData,
      session.therapist.specialization,
      selectedDateStr,
      billingServiceType
    );

    console.log('Fresh Matching Service with updated promo count:', matchingService);

    const sessionType = session.attendance?.session_type || session.session_type;

    const clientRate = getClientBillingRate(
      matchingService,
      getBillingRateDate(selectedDateStr, session)
    );

    const baseAmount = session.session_type === 'IE' ? 3500 :
    calculatePrice(
      clientRate,
      session.client?.pwd || false,
      matchingService?.vat_inclusive || false
    );

    const feeApplies = await shouldApplyReservationFee({
      clientId: session.client.id,
      sessionType,
      therapistId: session.therapist.id,
      therapistSpecialization: session.therapist.specialization,
      excludeAttendanceId: session.attendance?.id ?? null,
    });
    const reservationFee = getReservationFeeDeduction(matchingService, feeApplies);

    const hasOngoingPromo = matchingService?.promo_count > 0 && matchingService?.promo_count < 10;

    const computedAmountPaid = hasOngoingPromo ?
      (matchingService.promo_count === 0 ?
        calculatePrice(
          matchingService.base_rate * 10,
          session.client.pwd,
          matchingService.vat_inclusive
        ) :
        0
      ) : (baseAmount - reservationFee);

    const displayAmountPaid = session.attendance
      ? (hasOngoingPromo
          ? Number(session.attendance.amount_paid) || 0
          : grossToNetAmountPaid(session.attendance.amount_paid, reservationFee))
      : computedAmountPaid;

    console.log('Selected Session with fresh data: ', session);

    // Create an updated session object with fresh client services data
    const updatedSession = {
      ...session,
      client: {
        ...session.client,
        client_services: clientServicesData
      }
    };

    setSelectedSession(updatedSession); // Use the updated session with fresh data
    setAttendanceForm({
      status: session.attendance?.status || 'present',
      notes: session.attendance?.notes || '',
      amountPaid: displayAmountPaid.toString(),
      orNumber: session.attendance?.or_number || '',
      centerOrNumber: session.attendance?.center_or_number || session.attendance?.or_number || '',
      therapistOrNumber: session.attendance?.therapist_or_number || '',
      orName: session.client.or_name || '',
      isPromo: hasOngoingPromo || Boolean(session.attendance?.promo_session),
      isPaid: session.attendance?.is_paid ?? true,
      noMakeupRequired: session.is_makeup ? true : (session.attendance?.madeup_for === null ? true : false)
    });
    setDocumentStatus(getDocumentStatusForClient(session.client));

    if (typeof setPaidNoShowFee === 'function') {
      setPaidNoShowFee(session.attendance?.paid_noshow === true);
    }
    if (typeof setCancelledMakeupByTherapist === 'function') {
      setCancelledMakeupByTherapist(Boolean(session.attendance?.makeup_fee_voided_by_therapist));
    }
    if (typeof setRescheduleMakeup === 'function') {
      setRescheduleMakeup(options?.rescheduleMakeup === true);
    }
    if (typeof setGraceCancellation === 'function') {
      setGraceCancellation(Boolean(session.attendance?.grace_cancellation));
    }
    if (typeof setWaivedLateCancellation === 'function') {
      setWaivedLateCancellation(Boolean(session.attendance?.waived_late_cancellation));
    }
    
    setIsAttendanceModalOpen(true);
  }, [
    session, 
    selectedDate, 
    calculatePrice, 
    setSelectedSession, 
    setAttendanceForm,
    setDocumentStatus,
    setPaidNoShowFee,
    setCancelledMakeupByTherapist,
    setRescheduleMakeup,
    setGraceCancellation,
    setWaivedLateCancellation,
    setIsAttendanceModalOpen,
    onRefresh
  ]);
  
    const handleNotesClick = useCallback(() => {
      setSelectedSession(session);
      setSessionNotes(session.client.notes || '');
      setIsNotesModalOpen(true);
    }, [session, setSelectedSession, setSessionNotes, setIsNotesModalOpen]);

    const canMoveMakeupTime =
      session.is_makeup &&
      session.attendance?.status !== 'present';

    const handleUpdateMakeupTime = useCallback(async () => {
      if (!makeupTimeDraft || makeupTimeDraft === session.time) return;

      setUpdatingMakeupTime(true);
      try {
        const { error } = await supabase
          .from('sessions')
          .update({ time: makeupTimeDraft })
          .eq('id', session.id);

        if (error) throw error;

        toast({
          title: 'Makeup time updated',
          description: `Moved to ${formatTime(makeupTimeDraft)}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onRefresh?.();
      } catch (error) {
        toast({
          title: 'Error updating makeup time',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setUpdatingMakeupTime(false);
      }
    }, [makeupTimeDraft, session.id, session.time, formatTime, onRefresh, toast]);

    return (
    <Card size="sm" variant="outline" mb={2} bg={session.attendance?.status === 'present' ? 'green.50' : (session.attendance?.status === 'cancelled' || session.attendance?.status === 'absent') ? 'red.50' : 'white'} borderColor={session.attendance?.status === 'present' ? 'green.200' : (session.attendance?.status === 'cancelled' || session.attendance?.status === 'absent') ? 'red.200' : 'gray.200'}>
      <CardBody>
        <VStack align="start" spacing={1}>
          <VStack spacing={0} width="100%" pb={1}>
          <HStack justify="space-between" align="start" width="100%">
            <VStack align="start" spacing={0}>
                <Text fontWeight="bold">{session.client.name}</Text>
                {session.client.nickname && (
                <Text fontSize="sm" color="gray.500">
                    "{session.client.nickname}"
                </Text>
                )}
            </VStack>
            <HStack>
              {/* Only show session type badge if not Regular */}
              {((session.attendance && session.attendance.session_type !== 'Regular') ||
                (!session.attendance && session.session_type !== 'Regular')) && (
                <Badge colorScheme="green">
                  {session.attendance ? session.attendance.session_type : session.session_type}
                </Badge>
              )}
              
              {/* B2B Badge - Will be populated in useEffect */}
              {session.hasB2BSessions && (
                <Badge colorScheme="blue">Back-to-Back</Badge>
              )}
              
              {/* Makeup Session Badge */}
              {session.is_makeup && (
                <Badge colorScheme="purple">
                  {(session.attendance?.status === 'cancelled' || session.attendance?.status === 'absent')
                    ? `Supposed Makeup for ${session.makeup_for ? new Date(session.makeup_for).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Unknown'}`
                    : `Makeup for ${session.makeup_for ? new Date(session.makeup_for).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Unknown'}`
                  }
                </Badge>
              )}
              
              {session.client.pwd && <Badge colorScheme="purple">PWD</Badge>}
            </HStack>
          </HStack>
          
          <HStack justify="space-between" width="100%">
            <Text fontSize="sm" color="gray.600">
              {getClientDisplayAge(session.client) ?? '—'}yo {session.client.gender} - {session.client.diagnosis || 'No diagnosis'}
            </Text>
            <HStack spacing={2} mt={1}>
                <Tooltip label={`DR/PR: ${session.client.initial_document ? '✅' : '❌'}`}>
                    <Box>
                    <Text fontSize="3xs" borderWidth={2} borderColor={session.client.initial_document ? 'green.500' : 'red.500'}>
                        📄
                    </Text>
                    </Box>
                </Tooltip>
                <Tooltip label={`CA Given: ${session.client.given_ca ? '✅' : '❌'}`}>
                    <Box>
                    <Text fontSize="3xs" borderWidth={2} borderColor={session.client.given_ca ? 'green.500' : 'red.500'}>
                        📋
                    </Text>
                    </Box>
                </Tooltip>
                <Tooltip label={`CA Signed: ${session.client.signed_ca ? '✅' : '❌'}`}>
                    <Box>
                    <Text fontSize="3xs" borderWidth={2} borderColor={session.client.signed_ca ? 'green.500' : 'red.500'}>
                        ✍️
                    </Text>
                    </Box>
                </Tooltip>
                {session.client.pwd && (
                    <Tooltip label={`PWD Card: ${session.client.pwd_card ? '✅' : '❌'}`}>
                    <Box>
                        <Text fontSize="3xs" borderWidth={2} borderColor={session.client.pwd_card ? 'green.500' : 'red.500'}>
                        🆔
                        </Text>
                    </Box>
                    </Tooltip>
                )}
                {session.client.official_receipt && (
                <Tooltip label={`Official Receipt (${session.client.or_name})`}>
                    <Box>
                    <Text fontSize="3xs" borderWidth={2} borderColor="green.500">
                        🧾
                    </Text>
                    </Box>
                </Tooltip>
                )}
            </HStack>
          </HStack>
          </VStack>
          
          <Divider />
          
          <HStack width="100%" pt={2}>
            <Text fontSize="sm">
                <Text as="span" fontWeight="medium">Base Rate:</Text> {formatCurrency(session.base_rate)}
                {session.vat_inclusive && <Text as="span" color="gray.500"> (VAT incl.)</Text>}
            </Text>
            {session.client.pwd && (
                <Text fontSize="sm">
                <Text as="span" fontWeight="medium">PWD Rate:</Text> {formatCurrency(session.discounted_rate)}
                </Text>
            )}
          </HStack>

          <HStack>
            <Text fontSize="sm">
                <Text as="span" fontWeight="medium">Therapist:</Text> {session.therapist.name}
            </Text>
            <Badge colorScheme={session.service_type === 'FT' ? 'purple' : 'pink'}>
                {session.service_type}
            </Badge>
            {isSundayDate(getLocalDateString(selectedDate)) && (
              <Badge colorScheme="orange">Sunday</Badge>
            )}
            {shouldUseSplitReceipts(session.therapist, getLocalDateString(selectedDate)) &&
              session.client.official_receipt && (
              <Badge colorScheme="purple">Split Receipts</Badge>
            )}
          </HStack>

          <Text fontSize="sm">
            <Text as="span" fontWeight="medium">Guardian:</Text> {session.client.guardian_name}
          </Text>
          <Text fontSize="sm">
            <Text as="span" fontWeight="medium">Contact:</Text> {session.client.guardian_contact}
          </Text>
          
          <Text fontSize="sm">
            <Text as="span" fontWeight="medium">Notes:</Text> {session.client.notes}
          </Text>
          <HStack spacing={2} mt={2}>
          <Button
            size="sm"
            leftIcon={session.attendance ? <EditIcon /> : <CheckIcon />}
            colorScheme={session.attendance?.paid_noshow ? 'gray' : session.attendance ? 'gray' : 'green'}
            onClick={handleAttendanceClick}
            isDisabled={session.attendance?.paid_noshow}
            >
            {session.attendance?.paid_noshow 
              ? 'No-Show Fee Paid' 
              : session.attendance 
                ? 'Update Attendance' 
                : 'Mark Attendance'}
          </Button>
            <Tooltip label="Edit Client Notes" placement="top">
                <IconButton
                icon={<EditIcon />}
                size="sm"
                variant="outline"
                onClick={() => {
                    setSelectedSession(session);
                    setSessionNotes(session.client.notes || '');
                    setIsNotesModalOpen(true);
                }}
                aria-label="Edit notes"
                />
            </Tooltip>
          </HStack>

          {canMoveMakeupTime && (
            <Box mt={2} p={2} bg="blue.50" borderRadius="md" width="100%">
              <Text fontSize="xs" fontWeight="semibold" color="blue.800" mb={2}>
                Move Makeup Time
              </Text>
              <HStack spacing={2} flexWrap="wrap">
                <Select
                  size="sm"
                  maxW="160px"
                  value={makeupTimeDraft}
                  onChange={(e) => setMakeupTimeDraft(e.target.value)}
                >
                  {DAILY_TIME_SLOTS.map((time) => (
                    <option key={time} value={time}>
                      {formatTime(time)}
                    </option>
                  ))}
                </Select>
                <Button
                  size="sm"
                  colorScheme="blue"
                  leftIcon={<TimeIcon />}
                  onClick={handleUpdateMakeupTime}
                  isLoading={updatingMakeupTime}
                  isDisabled={!makeupTimeDraft || makeupTimeDraft === session.time}
                >
                  Update Time
                </Button>
              </HStack>
            </Box>
          )}
          

          {session.attendance && (
            <Box mt={2} p={2} bg={
              session.attendance?.paid_noshow ? "orange.50" : 
              (session.is_makeup && (session.attendance?.status === 'cancelled' || session.attendance?.status === 'absent')) ? "red.50" :
              session.attendance?.makeup_scheduled === true ? "blue.50" : 
              session.attendance?.makeup_scheduled === false ? "purple.50" : 
              "gray.50"
            } borderRadius="md" width="100%">
              <HStack flexWrap="wrap" spacing={2}>
              <Text fontSize="sm" fontWeight="medium">
                Status:{' '}
                <Badge colorScheme={getAttendanceStatusColorScheme(session.attendance.status)}>
                  {formatAttendanceStatusLabel(session.attendance.status)}
                </Badge>
              </Text>
              {session.attendance.grace_cancellation && session.attendance.status === 'absent' && (
                <Badge colorScheme="teal" variant="solid" size="sm">
                  Grace Cancellation
                </Badge>
              )}
              {session.attendance.waived_late_cancellation && session.attendance.status === 'absent' && (
                <Badge colorScheme="cyan" variant="solid" size="sm">
                  Waived Late Cancellation
                </Badge>
              )}
              {session.attendance?.status === 'present' && !session.attendance.is_paid && (
                <Badge colorScheme="orange">Unpaid</Badge>
              )}
              {session.attendance?.paid_noshow && (
                <Badge colorScheme="orange">No-Show Fee Paid (₱950)</Badge>
              )}
              {!session.is_makeup &&
                isCancelledOrLateCancellationStatus(session.attendance?.status) &&
                session.attendance?.voided_linked_makeup_on && (
                  <>
                    <Badge colorScheme="purple" variant="outline" size="sm">
                      Supposed Makeup on{' '}
                      {formatMakeupBadgeDate(session.attendance.voided_linked_makeup_on)}{' '}
                      cancelled by therapist
                    </Badge>
                    <Badge colorScheme="green" variant="outline" size="sm">
                      Makeup no longer required
                    </Badge>
                  </>
                )}
              {session.attendance?.makeup_scheduled === true &&
                !session.attendance?.voided_linked_makeup_on &&
                !session.attendance?.paid_noshow && (
                <Badge colorScheme="blue">
                  Makeup Scheduled{scheduledMakeupDate ? ` (${new Date(scheduledMakeupDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})` : ''}
                </Badge>
              )}
              {session.attendance?.makeup_scheduled === false && !session.attendance?.paid_noshow && !session.is_makeup && (
                <Badge colorScheme="purple">Makeup Pending</Badge>
              )}
              {session.is_makeup &&
                (session.attendance?.status === 'cancelled' || session.attendance?.status === 'absent') &&
                session.makeup_for && (
                  <Badge colorScheme="cyan" variant="outline" size="sm">
                    Supposed Makeup for {formatMakeupBadgeDate(session.makeup_for)}
                  </Badge>
                )}
              {session.is_makeup &&
                (session.attendance?.status === 'cancelled' || session.attendance?.status === 'absent') &&
                session.attendance?.makeup_fee_voided_by_therapist &&
                session.makeup_for && (
                  <Badge colorScheme="purple" variant="outline" size="sm">
                    Makeup for {formatMakeupBadgeDate(session.makeup_for)} cancelled by therapist
                  </Badge>
                )}
              {session.is_makeup &&
                (session.attendance?.status === 'cancelled' || session.attendance?.status === 'absent') &&
                !session.attendance?.makeup_fee_voided_by_therapist &&
                session.attendance?.paid_noshow === false && (
                <Badge colorScheme="red">Pending No-Show Fee</Badge>
              )}
              </HStack>
              {session.is_makeup &&
                (session.attendance?.status === 'cancelled' || session.attendance?.status === 'absent') &&
                !session.attendance?.makeup_fee_voided_by_therapist &&
                session.attendance?.paid_noshow === false && (
                <Button
                  mt={2}
                  size="sm"
                  colorScheme="green"
                  variant="outline"
                  onClick={() => handleAttendanceClick({ rescheduleMakeup: true })}
                >
                  Reschedule (waive no-show fee)
                </Button>
              )}
              {session.attendance.status === 'present' && session.attendance.is_paid && (
                <>
                  <Text fontSize="sm">
                    Paid: {formatCurrency(session.attendance.amount_paid)}
                  </Text>
                  {session.attendance.center_fee != null && session.attendance.therapist_fee != null ? (
                    <>
                      <Text fontSize="sm" color="gray.600">
                        Center: {formatCurrency(session.attendance.center_fee)} / Therapist: {formatCurrency(session.attendance.therapist_fee)}
                      </Text>
                      {session.attendance.center_or_number && (
                        <Text fontSize="sm">Center OR#: {session.attendance.center_or_number}</Text>
                      )}
                      {session.attendance.therapist_or_number && (
                        <Text fontSize="sm">Therapist OR#: {session.attendance.therapist_or_number}</Text>
                      )}
                    </>
                  ) : session.attendance.or_number && (
                    <Text fontSize="sm">
                      OR#: {session.attendance.or_number}
                    </Text>
                  )}
                </>
              )}
              {session.attendance.notes && (
                <Text fontSize="sm">Session Notes: {session.attendance.notes}</Text>
              )}
              {nextSession && (
                <Text fontSize="xs" fontWeight="bold" mt={2}>
                    Next Session: {nextSession.nextDate.toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                    })} ({nextSession.day}) at {formatTime(nextSession.time)}
                </Text>
              )}
              {session.attendance && session.attendance.status === 'absent' && session.attendance.final_amount === 950 && !session.attendance.grace_cancellation && !session.attendance.waived_late_cancellation && (
                <Button
                    mt={3}
                    size="sm"
                    colorScheme="red"
                    onClick={async () => {
                    try {
                        const { error } = await supabase
                        .from('session_attendance')
                        .update({ 
                            final_amount: 0,
                            amount_paid: 950,
                            is_paid: true,  // Add this line
                            payment_date: new Date().toISOString()  // Add this line
                        })
                        .eq('id', session.attendance.id);

                        if (error) throw error;
                        
                        // Call the appropriate refresh function based on current view
                        if (typeof onRefresh === 'function') {
                            await onRefresh();
                        }
                        
                        // If we're in unpaid view, also refresh unpaid sessions
                        if (window.location.hash?.includes('unpaid') || 
                            document.querySelector('[aria-selected="true"]')?.textContent === 'Unpaid Sessions') {
                            // Trigger unpaid sessions refresh by dispatching a custom event
                            window.dispatchEvent(new CustomEvent('refreshUnpaidSessions'));
                        }
                        
                        toast({
                        title: 'Absence fee collected',
                        status: 'success',
                        duration: 3000
                        });
                    } catch (error) {
                        toast({
                        title: 'Error updating payment',
                        description: error.message,
                        status: 'error',
                        duration: 5000
                        });
                    }
                    }}
                    isDisabled={session.attendance.final_amount === 0}
                >
                    {session.attendance.final_amount === 0 ? 'Collected' : 'Collect ₱950'}
                </Button>
              )}
              {session.attendance && session.attendance.status === 'present' && !session.attendance.is_paid && (
                <Button
                    mt={3}
                    size="sm"
                    colorScheme="red"
                    onClick={async () => {
                        try {
                            const { error } = await supabase
                                .from('session_attendance')
                                .update({ 
                                    is_paid: true,
                                    payment_date: new Date().toISOString()
                                })
                                .eq('id', session.attendance.id);

                            if (error) throw error;
                            
                            // Call the appropriate refresh function based on current view
                            if (typeof onRefresh === 'function') {
                                await onRefresh();
                            }
                            
                            // If we're in unpaid view, also refresh unpaid sessions
                            if (window.location.hash?.includes('unpaid') || 
                                document.querySelector('[aria-selected="true"]')?.textContent === 'Unpaid Sessions') {
                                // Trigger unpaid sessions refresh by dispatching a custom event
                                window.dispatchEvent(new CustomEvent('refreshUnpaidSessions'));
                            }
                            
                            toast({
                                title: 'Payment collected',
                                status: 'success',
                                duration: 3000
                            });
                        } catch (error) {
                            toast({
                                title: 'Error updating payment',
                                description: error.message,
                                status: 'error',
                                duration: 5000
                            });
                        }
                    }}
                >
                    Collect {formatCurrency(collectableAmount ?? session.attendance.amount_paid)}
                </Button>
              )}
            </Box>
          )}
        </VStack>
      </CardBody>
    </Card>
    );
  }, (prevProps, nextProps) => {
    // Simplified comparison - remove the problematic props
    const sessionIdSame = prevProps.session.id === nextProps.session.id;
    const attendanceIdSame = prevProps.session.attendance?.id === nextProps.session.attendance?.id;
    const attendanceStatusSame = prevProps.session.attendance?.status === nextProps.session.attendance?.status;
    const paidNoshowSame = prevProps.session.attendance?.paid_noshow === nextProps.session.attendance?.paid_noshow;
    const isPaidSame = prevProps.session.attendance?.is_paid === nextProps.session.attendance?.is_paid;
    const sessionTimeSame = prevProps.session.time === nextProps.session.time;
    const isMakeupSame = prevProps.session.is_makeup === nextProps.session.is_makeup;
    const makeupScheduledSame = prevProps.session.attendance?.makeup_scheduled === nextProps.session.attendance?.makeup_scheduled;
    const makeupVoidSame =
      prevProps.session.attendance?.voided_linked_makeup_on ===
      nextProps.session.attendance?.voided_linked_makeup_on;
    const therapistWaivedFeeSame =
      prevProps.session.attendance?.makeup_fee_voided_by_therapist ===
      nextProps.session.attendance?.makeup_fee_voided_by_therapist;
    const graceCancellationSame =
      prevProps.session.attendance?.grace_cancellation ===
      nextProps.session.attendance?.grace_cancellation;
    const waivedLateCancellationSame =
      prevProps.session.attendance?.waived_late_cancellation ===
      nextProps.session.attendance?.waived_late_cancellation;
    
    console.log(`🔍 SessionCard memo check for ${prevProps.session.client.name}:`, {
      sessionIdSame,
      attendanceIdSame,
      attendanceStatusSame,
      paidNoshowSame,
      isPaidSame,
      makeupScheduledSame,
      makeupVoidSame,
      therapistWaivedFeeSame,
      graceCancellationSame,
      waivedLateCancellationSame,
    });
    
    return (
      sessionIdSame &&
      attendanceIdSame &&
      attendanceStatusSame &&
      paidNoshowSame &&
      isPaidSame &&
      sessionTimeSame &&
      isMakeupSame &&
      makeupScheduledSame &&
      makeupVoidSame &&
      therapistWaivedFeeSame &&
      graceCancellationSame &&
      waivedLateCancellationSame
    );
  });

// Add display name for debugging
SessionCard.displayName = 'SessionCard';

const VATCalculatorModal = ({ isOpen, onClose }) => {
  const [amount, setAmount] = useState('');
  const [isPWD, setIsPWD] = useState(false);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(value);
  };

  const calculations = useMemo(() => {
    const numAmount = parseFloat(amount) || 0;
    const preVatAmount = numAmount / 1.12;
    const vatAmount = numAmount - preVatAmount;
    const pwdDiscount = isPWD ? (preVatAmount * 0.2) : 0;
    const finalAmount = (preVatAmount - pwdDiscount) + vatAmount;

    return {
      totalSales: numAmount,
      preVatAmount,
      vatAmount,
      pwdDiscount,
      finalAmount
    };
  }, [amount, isPWD]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>VAT Calculator</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl>
              <FormLabel>Amount (VAT Inclusive)</FormLabel>
              <NumberInput
                value={amount}
                onChange={(value) => setAmount(value)}
                min={0}
              >
                <NumberInputField placeholder="Enter amount" />
              </NumberInput>
            </FormControl>

            <FormControl>
              <Checkbox
                isChecked={isPWD}
                onChange={(e) => setIsPWD(e.target.checked)}
              >
                Apply PWD Discount (20%)
              </Checkbox>
            </FormControl>

            <Box width="100%" p={4} borderWidth={1} borderRadius="md">
              <VStack align="start" spacing={2}>
                <HStack justify="space-between" width="100%">
                  <Text>Total Sales (VAT Inclusive):</Text>
                  <Text>{formatCurrency(calculations.totalSales)}</Text>
                </HStack>
                <HStack justify="space-between" width="100%">
                  <Text>Less: VAT:</Text>
                  <Text>{formatCurrency(calculations.vatAmount)}</Text>
                </HStack>
                <HStack justify="space-between" width="100%">
                  <Text>Amount Net of VAT:</Text>
                  <Text>{formatCurrency(calculations.preVatAmount)}</Text>
                </HStack>
                {isPWD && (
                  <HStack justify="space-between" width="100%">
                    <Text>Less: Discount (PWD 20%):</Text>
                    <Text>{formatCurrency(calculations.pwdDiscount)}</Text>
                  </HStack>
                )}
                <HStack justify="space-between" width="100%">
                  <Text>Add: VAT:</Text>
                  <Text>{formatCurrency(calculations.vatAmount)}</Text>
                </HStack>
                <Divider />
                <HStack justify="space-between" width="100%">
                  <Text fontWeight="bold">Total Amount Due:</Text>
                  <Text fontWeight="bold">{formatCurrency(calculations.finalAmount)}</Text>
                </HStack>
              </VStack>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const ReservationFeeDisplay = ({ selectedSession, selectedDate, formatCurrency }) => {
  const [reservationFeeText, setReservationFeeText] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkReservationFeeStatus = async () => {
      try {
        setIsLoading(true);

        const selectedDateStr = getLocalDateString(selectedDate);
        const matchingService = findMatchingClientService(
          selectedSession.client?.client_services,
          selectedSession.therapist.specialization,
          selectedDateStr
        );

        const reservationFee = Number(matchingService?.reservation_fee) || 0;

        if (reservationFee === 0) {
          setReservationFeeText('No reservation fee applied');
          return;
        }

        const sessionType =
          selectedSession.attendance?.session_type || selectedSession.session_type;

        if (sessionType !== 'IE' && sessionType !== 'Endorsement') {
          setReservationFeeText('No reservation fee applied');
          return;
        }

        const feeApplies = await shouldApplyReservationFee({
          clientId: selectedSession.client.id,
          sessionType,
          therapistId: selectedSession.therapist.id,
          therapistSpecialization: selectedSession.therapist.specialization,
          excludeAttendanceId: selectedSession.attendance?.id ?? null,
        });

        if (!feeApplies) {
          setReservationFeeText('Reservation fee already paid for this service type');
          return;
        }

        setReservationFeeText(
          `Reservation fee of ${formatCurrency(reservationFee)} deducted from total amount`
        );
      } catch (error) {
        console.error('Error checking reservation fee status:', error);
        setReservationFeeText('Error checking reservation fee status');
      } finally {
        setIsLoading(false);
      }
    };

    checkReservationFeeStatus();
  }, [selectedSession, selectedDate, formatCurrency]);

  if (isLoading) {
    return <Spinner size="sm" />;
  }

  return (
    <Text fontSize="sm" color="gray.600" mb={2}>
      {reservationFeeText}
    </Text>
  );
};

const DailySchedule = ({ isVisible = true, showSessionTrends = false }) => {
  console.log('🔄 DailySchedule component re-rendering');
  const dailyScheduleViews = useMemo(
    () => (showSessionTrends
      ? [...BASE_DAILY_SCHEDULE_VIEWS, SESSION_TRENDS_VIEW]
      : BASE_DAILY_SCHEDULE_VIEWS),
    [showSessionTrends]
  );
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [isAttendanceModalOpen, setIsAttendanceModalOpen] = useState(false);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [attendanceForm, setAttendanceForm] = useState({
    status: 'present',
    notes: '',
    amountPaid: '',
    orNumber: '',
    centerOrNumber: '',
    therapistOrNumber: '',
    orName: '',
    isPromo: false,
    isPaid: true,
    noMakeupRequired: false
  });
  const [receiptSplitPreview, setReceiptSplitPreview] = useState(null);
  const [sameDayReceiptSummary, setSameDayReceiptSummary] = useState(null);
  const [sessionNotes, setSessionNotes] = useState('');
  const [view, setView] = useState('clients');
  const [selectedTherapist, setSelectedTherapist] = useState(null);
  const reminderFetchIdRef = useRef(0);

  useEffect(() => {
    if (!showSessionTrends && view === SESSION_TRENDS_VIEW) {
      setView('clients');
    }
  }, [showSessionTrends, view]);
  const [therapists, setTherapists] = useState([]);
  const [isOneTimeModalOpen, setIsOneTimeModalOpen] = useState(false);
  const [oneTimeSessionForm, setOneTimeSessionForm] = useState({
    therapist_id: '',
    client_id: '',
    time: '',
    notes: '',
    pending_makeup_id: '' // attendance_id of the cancelled session to make up for
  });
  const [availableClients, setAvailableClients] = useState([]);
  const [availableTherapists, setAvailableTherapists] = useState([]);
  // Pending makeups for one-time session modal
  const [clientPendingMakeups, setClientPendingMakeups] = useState([]);
  const [loadingClientMakeups, setLoadingClientMakeups] = useState(false);
  const [unpaidSessions, setUnpaidSessions] = useState([]);
  const [unpaidLoading, setUnpaidLoading] = useState(false);
  const [unpaidDateRange, setUnpaidDateRange] = useState(30); // days to look back
  const [totalReceivables90, setTotalReceivables90] = useState({ count: 0, amount: 0 });
  // Pending Makeup Sessions states
  const [pendingMakeupSessions, setPendingMakeupSessions] = useState([]);
  const [pendingMakeupLoading, setPendingMakeupLoading] = useState(false);
  const [pendingMakeupDateRange, setPendingMakeupDateRange] = useState(30); // days to look back
  const [pendingMakeupViewMode, setPendingMakeupViewMode] = useState('by-date'); // 'by-date' | 'by-client'
  const [scheduledFutureMakeupSessions, setScheduledFutureMakeupSessions] = useState([]);
  const [newClientReminderSessions, setNewClientReminderSessions] = useState([]);
  const [newClientRemindersLoading, setNewClientRemindersLoading] = useState(false);
  const [makeupOpportunities, setMakeupOpportunities] = useState([]);
  const [makeupOpportunitiesLoading, setMakeupOpportunitiesLoading] = useState(false);
  // Add this near your other state declarations
  const [isVATCalculatorOpen, setIsVATCalculatorOpen] = useState(false);
  const [isSubmittingAttendance, setIsSubmittingAttendance] = useState(false);
  // Makeup session availability states
  const [availableMakeupSlots, setAvailableMakeupSlots] = useState([]);
  const [fetchingMakeupSlots, setFetchingMakeupSlots] = useState(false);
  const [selectedMakeupSlot, setSelectedMakeupSlot] = useState(null);
  const [existingLinkedMakeup, setExistingLinkedMakeup] = useState(null);
  const [paidNoShowFee, setPaidNoShowFee] = useState(false);
  const [cancelledMakeupByTherapist, setCancelledMakeupByTherapist] = useState(false);
  const [rescheduleMakeup, setRescheduleMakeup] = useState(false);
  const [rescheduleTherapistId, setRescheduleTherapistId] = useState('');
  const [rescheduleTherapistOptions, setRescheduleTherapistOptions] = useState([]);
  const [graceCancellation, setGraceCancellation] = useState(false);
  const [waivedLateCancellation, setWaivedLateCancellation] = useState(false);
  const [graceCancellationUsedCount, setGraceCancellationUsedCount] = useState(0);
  const toast = useToast();

  const DAYS = useMemo(() => ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'], []);
  const TIME_SLOTS = useMemo(() => DAILY_TIME_SLOTS, []);

  const [documentStatus, setDocumentStatus] = useState(DEFAULT_DOCUMENT_STATUS);

  const handleSetSelectedSession = useCallback((session) => {
    setSelectedSession(session);
  }, []);
  
  const handleSetAttendanceForm = useCallback((form) => {
    setAttendanceForm(form);
  }, []);
  
  const handleSetIsAttendanceModalOpen = useCallback((isOpen) => {
    setIsAttendanceModalOpen(isOpen);
  }, []);
  
  const handleSetSessionNotes = useCallback((notes) => {
    setSessionNotes(notes);
  }, []);
  
  const handleSetIsNotesModalOpen = useCallback((isOpen) => {
    setIsNotesModalOpen(isOpen);
  }, []);

  const calculatePrice = useCallback((baseRate, isPwd, isVatInclusive) => {
    if (!baseRate) return 0;
      
    const VAT_RATE = 0.12;
      
    if (isVatInclusive) {
      const preVatRate = Math.round(baseRate / (1 + VAT_RATE) * 100) / 100;
      if (isPwd) {
        const discountedPreVatRate = Math.round(preVatRate * 0.8 * 100) / 100;
        return Math.round((discountedPreVatRate + (baseRate-preVatRate))* 100) / 100;
      }
      return baseRate;
    }
    return isPwd ? Math.round(baseRate * 0.8 * 100) / 100 : baseRate;
  }, []);

  useEffect(() => {
    if (isOneTimeModalOpen) {
      fetchAvailableClients();
      fetchAvailableTherapists();
    } else {
      // Reset form and pending makeups when modal closes
      setClientPendingMakeups([]);
    }
  }, [isOneTimeModalOpen]);

  // Fetch pending makeups when therapist and client are selected in one-time modal
  useEffect(() => {
    const fetchClientPendingMakeups = async () => {
      if (!oneTimeSessionForm.therapist_id || !oneTimeSessionForm.client_id) {
        setClientPendingMakeups([]);
        return;
      }

      const selectedTherapist = availableTherapists.find(
        (therapist) => therapist.id === oneTimeSessionForm.therapist_id
      );

      if (!selectedTherapist?.specialization) {
        setClientPendingMakeups([]);
        return;
      }

      setLoadingClientMakeups(true);
      try {
        const { data, error } = await supabase
          .from('session_attendance')
          .select(`
            id,
            attendance_date,
            status,
            notes,
            makeup_scheduled,
            therapist:therapists (
              id,
              name,
              specialization
            ),
            session:sessions!session_id (
              id,
              time,
              day
            )
          `)
          .eq('client_id', oneTimeSessionForm.client_id)
          .eq('madeup_for', false)
          .in('status', ['cancelled', 'absent'])
          .order('attendance_date', { ascending: false });

        if (error) throw error;

        const therapyTypePendingMakeups = (data || []).filter((record) => {
          if (record.makeup_scheduled === true) return false;
          return record.therapist?.specialization === selectedTherapist.specialization;
        });

        setClientPendingMakeups(therapyTypePendingMakeups);
      } catch (error) {
        console.error('Error fetching client pending makeups:', error);
        setClientPendingMakeups([]);
      } finally {
        setLoadingClientMakeups(false);
      }
    };

    fetchClientPendingMakeups();
  }, [oneTimeSessionForm.therapist_id, oneTimeSessionForm.client_id, availableTherapists]);

  useEffect(() => {
    const computeReceiptSplitPreview = async () => {
      if (!isAttendanceModalOpen || !selectedSession || attendanceForm.status !== 'present') {
        setReceiptSplitPreview(null);
        return;
      }

      const sessionDateStr = getLocalDateString(selectedDate);
      if (!shouldUseSplitReceipts(selectedSession.therapist, sessionDateStr)) {
        setReceiptSplitPreview(null);
        return;
      }

      const totalDue = parseFloat(attendanceForm.amountPaid) || 0;
      const billingServiceType = resolveBillingServiceType(
        selectedSession,
        selectedSession.therapist,
        selectedSession.client?.client_services,
        sessionDateStr
      );
      let therapistFee = 0;

      if (selectedSession.session_type === 'IE') {
        therapistFee = 2200;
      } else {
        therapistFee = await getSessionServiceFeeAsync({
          session: {
            ...selectedSession,
            billing_service_type: billingServiceType,
          },
          therapistSpecialization: selectedSession.therapist.specialization,
          therapistId: selectedSession.therapist.id,
          date: sessionDateStr,
        }) ?? 0;
      }
      const matchingService = findMatchingClientService(
        selectedSession.client?.client_services,
        selectedSession.therapist.specialization,
        sessionDateStr,
        billingServiceType
      );

      setReceiptSplitPreview(await getPromoAwareReceiptSplit({
        selectedSession,
        matchingService,
        attendanceForm,
        sessionDateStr,
        totalDue,
        therapistFee,
        billingServiceType,
      }));
    };

    computeReceiptSplitPreview();
  }, [
    isAttendanceModalOpen,
    selectedSession,
    attendanceForm.status,
    attendanceForm.amountPaid,
    attendanceForm.isPromo,
    selectedDate,
  ]);

  useEffect(() => {
    const computeSameDayReceiptSummary = async () => {
      if (!isAttendanceModalOpen || !selectedSession || attendanceForm.status !== 'present') {
        setSameDayReceiptSummary(null);
        return;
      }

      const sessionDateStr = getLocalDateString(selectedDate);
      const allClientSessions = sessions.filter(
        (session) =>
          session.client.id === selectedSession.client.id &&
          session.attendance?.status !== 'cancelled' &&
          session.attendance?.status !== 'absent'
      );

      const eligibleSessions = allClientSessions.filter((session) => {
        const billingServiceType = resolveBillingServiceType(
          session,
          session.therapist,
          session.client.client_services,
          sessionDateStr
        );
        const matchingService = findMatchingClientService(
          session.client.client_services,
          session.therapist.specialization,
          sessionDateStr,
          billingServiceType
        );

        return !isPromoReceiptSession({
          session,
          matchingService,
          isCurrentSession: session.id === selectedSession.id,
          isPromoFormValue: attendanceForm.isPromo,
        });
      });

      const excludedPromoCount = allClientSessions.length - eligibleSessions.length;

      if (eligibleSessions.length < 2) {
        setSameDayReceiptSummary(null);
        return;
      }

      const mode = getSameDayReceiptMode(eligibleSessions, sessionDateStr);
      if (mode === 'mixed') {
        setSameDayReceiptSummary({
          mode,
          sessionCount: eligibleSessions.length,
          excludedPromoCount,
        });
        return;
      }

      const sessionLines = await Promise.all(
        eligibleSessions.map(async (session) => {
          const isCurrent = session.id === selectedSession.id;
          const totalDue = isCurrent
            ? parseFloat(attendanceForm.amountPaid) || 0
            : session.attendance?.status === 'present'
              ? Number(session.attendance.amount_paid) || 0
              : Number(session.discounted_rate) || 0;

          const billingServiceType = resolveBillingServiceType(
            session,
            session.therapist,
            session.client.client_services,
            sessionDateStr
          );
          const matchingService = findMatchingClientService(
            session.client.client_services,
            session.therapist.specialization,
            sessionDateStr,
            billingServiceType
          );

          let therapistFee = 0;
          if (session.session_type === 'IE') {
            therapistFee = 2200;
          } else {
            therapistFee =
              (await getSessionServiceFeeAsync({
                session: {
                  ...session,
                  billing_service_type: billingServiceType,
                },
                therapistSpecialization: session.therapist.specialization,
                therapistId: session.therapist.id,
                date: getBillingRateDate(sessionDateStr, session),
              })) ?? 0;
          }

          return {
            sessionId: session.id,
            therapistId: session.therapist.id,
            therapistName: session.therapist.name,
            serviceType: session.service_type || billingServiceType,
            sessionType: session.session_type,
            time: session.time,
            totalDue,
            baseRate: session.session_type === 'IE' ? 3500 : Number(session.base_rate) || 0,
            therapistFee,
            isVatInclusive: matchingService?.vat_inclusive,
            isCurrent,
          };
        })
      );

      const showCenterVat = sessionLines.every((line) => line.isVatInclusive);
      const sameTherapist = sessionLines.every(
        (line) => line.therapistId === sessionLines[0]?.therapistId
      );

      if (mode === 'split') {
        setSameDayReceiptSummary({
          mode,
          sessionCount: eligibleSessions.length,
          excludedPromoCount,
          showCenterVat,
          sameTherapist,
          ...buildCombinedSplitReceiptSummary(sessionLines),
        });
        return;
      }

      setSameDayReceiptSummary({
        mode,
        sessionCount: eligibleSessions.length,
        excludedPromoCount,
        showCenterVat,
        isPwd: Boolean(selectedSession.client?.pwd),
        ...buildCombinedCenterReceiptSummary(sessionLines, {
          isPwd: Boolean(selectedSession.client?.pwd),
        }),
      });
    };

    computeSameDayReceiptSummary();
  }, [
    isAttendanceModalOpen,
    selectedSession,
    sessions,
    attendanceForm.status,
    attendanceForm.amountPaid,
    attendanceForm.isPromo,
    selectedDate,
  ]);

  useEffect(() => {
    if (view === 'unpaid') {
      fetchUnpaidSessions();
    }
  }, [view, unpaidDateRange]);

  useEffect(() => {
    if (view === 'pending-makeup') {
      fetchPendingMakeupSessions();
    }
  }, [view]);

  useEffect(() => {
    if (view === 'new-client-reminders' && isVisible) {
      fetchNewClientReminderSessions();
    }
  }, [view, selectedDate, isVisible]);

  useEffect(() => {
    if (isVisible) {
      fetchMakeupOpportunities();
    }
  }, [selectedDate, isVisible]);

  useEffect(() => {
    // Only run effects when component is visible
    if (!isVisible) {
      console.log('🔇 DailySchedule paused - not visible');
      return;
    }

    const fetchTherapists = async () => {
        const { data } = await supabase
          .from('sessions')
          .select(`
            therapist:therapists (
              id,
              name,
              specialization
            )
          `)
          .eq('day', DAYS[selectedDate.getDay()])
          .lte('valid_from', getLocalDateString(selectedDate))
          .or(`valid_until.is.null,valid_until.gte.${getLocalDateString(selectedDate)}`);
  
        const uniqueTherapists = [...new Set(data?.map(s => s.therapist).filter(Boolean)
          .map(t => JSON.stringify(t)))]
          .map(t => JSON.parse(t));
  
        setTherapists(uniqueTherapists);
        if (uniqueTherapists.length > 0 && !selectedTherapist) {
          setSelectedTherapist(uniqueTherapists[0].id);
        }
      };
  
      fetchTherapists();
      fetchSessions();
  }, [selectedDate, isVisible]);

  const fetchAvailableClients = async () => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select(`
          id,
          name,
          nickname,
          diagnosis,
          pwd,
          status,
          client_services (*)
        `)
        .eq('status', 'Active');
  
      if (error) throw error;
      setAvailableClients(data || []);
    } catch (error) {
      toast({
        title: 'Error fetching clients',
        description: error.message,
        status: 'error',
        duration: 5000
      });
    }
  };

  const fetchAvailableTherapists = async () => {
    try {
      let { data, error } = await supabase
        .from('therapists')
        .select('id, name, nickname, specialization, status')
        .in('status', ['active', 'Active'])
        .order('name');

      if (error?.message?.includes('status')) {
        ({ data, error } = await supabase
          .from('therapists')
          .select('id, name, nickname, specialization')
          .order('name'));
      }

      if (error) throw error;
      setAvailableTherapists(data || []);
    } catch (error) {
      toast({
        title: 'Error fetching therapists',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const fetchUnpaidSessions = async () => {
    console.time('🔄 fetchUnpaidSessions - Total Time');
    try {
      setUnpaidLoading(true);
      
      // Calculate date range for performance optimization
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - unpaidDateRange);
      
      const startDateStr = getLocalDateString(startDate);
      const endDateStr = getLocalDateString(endDate);

      console.time('📊 Supabase - Unpaid Sessions Query');
      console.log(`📈 Querying unpaid sessions from ${startDateStr} to ${endDateStr}`);

      const { data, error } = await supabase
        .from('session_attendance')
        .select(`
          *,
          session:sessions!session_id (
            id,
            day,
            time,
            session_type,
            valid_from,
            valid_until
          ),
          client:clients (
            id,
            name,
            nickname,
            age,
            birthdate,
            gender,
            diagnosis,
            guardian_name,
            guardian_contact,
            pwd,
            pwd_number,
            notes,
            initial_document,
            given_ca,
            signed_ca,
            signed_cf,
            pwd_card,
            official_receipt,
            or_name,
            client_services (*)
          ),
          therapist:therapists (
            id,
            name,
            specialization
          )
        `)
        .eq('is_paid', false)
        .eq('status', 'present')  // Only present sessions that are unpaid
        .gte('attendance_date', startDateStr)
        .lte('attendance_date', endDateStr)
        .order('attendance_date', { ascending: false });

        console.timeEnd('📊 Supabase - Unpaid Sessions Query');

      if (error) throw error;

      console.time('🔄 Data Processing - Unpaid Sessions');
      console.log(`📈 Processing ${data?.length || 0} unpaid sessions`);

      // Process the data to add calculated fields
      const processedSessions = data?.map(record => {
        const matchingService = record.client?.client_services?.find(service => {
          const specializationToService = {
            'Speech Therapy': 'ST',
            'Occupational Therapy': 'OT',
            'Physical Therapy': 'PT',
            'Play Group': 'PG'
          };
          const serviceMatches = service.service_type === specializationToService[record.therapist.specialization];
          const dateMatches = (!service.pricing_valid_from || service.pricing_valid_from <= record.attendance_date) &&
                            (!service.pricing_valid_until || service.pricing_valid_until >= record.attendance_date);
          return serviceMatches && dateMatches;
        });

        return {
          ...record.session,
          client: record.client,
          therapist: record.therapist,
          attendance: {
            ...record,
            session: undefined,
            client: undefined,
            therapist: undefined
          },
          base_rate: getClientBillingRate(
            matchingService,
            getBillingRateDate(record.attendance_date, record.session)
          ),
          discounted_rate: calculatePrice(
            getClientBillingRate(
              matchingService,
              getBillingRateDate(record.attendance_date, record.session)
            ),
            record.client?.pwd || false,
            matchingService?.vat_inclusive || false
          ),
          service_type: matchingService?.service_type || 'N/A',
          // Add attendance_date for grouping
          attendance_date: record.attendance_date
        };
      }) || [];
      console.timeEnd('🔄 Data Processing - Unpaid Sessions');

      setUnpaidSessions(processedSessions);

      // Fixed 90-day total receivables (independent of list filter)
      const receivablesStartDate = new Date();
      receivablesStartDate.setDate(receivablesStartDate.getDate() - 90);
      const receivablesStartStr = getLocalDateString(receivablesStartDate);

      const { data: receivablesData, error: receivablesError } = await supabase
        .from('session_attendance')
        .select(`
          attendance_date,
          client:clients (
            pwd,
            client_services (*)
          ),
          therapist:therapists (
            specialization
          ),
          session:sessions (
            is_makeup,
            makeup_for
          )
        `)
        .eq('is_paid', false)
        .eq('status', 'present')
        .gte('attendance_date', receivablesStartStr)
        .lte('attendance_date', endDateStr);

      if (receivablesError) throw receivablesError;

      const receivablesTotal = (receivablesData || []).reduce((sum, record) => {
        const matchingService = record.client?.client_services?.find((service) => {
          const specializationToService = {
            'Speech Therapy': 'ST',
            'Occupational Therapy': 'OT',
            'Physical Therapy': 'PT',
            'Play Group': 'PG',
          };
          const serviceMatches =
            service.service_type === specializationToService[record.therapist?.specialization];
          const dateMatches =
            (!service.pricing_valid_from || service.pricing_valid_from <= record.attendance_date) &&
            (!service.pricing_valid_until || service.pricing_valid_until >= record.attendance_date);
          return serviceMatches && dateMatches;
        });

        const baseRate = getClientBillingRate(
          matchingService,
          getBillingRateDate(record.attendance_date, record.session)
        );
        const amount = calculatePrice(
          baseRate,
          record.client?.pwd || false,
          matchingService?.vat_inclusive || false
        );
        return sum + amount;
      }, 0);

      setTotalReceivables90({
        count: receivablesData?.length || 0,
        amount: receivablesTotal,
      });
    } catch (error) {
      toast({
        title: 'Error fetching unpaid sessions',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUnpaidLoading(false);
      console.timeEnd('🔄 fetchUnpaidSessions - Total Time');
    }
  };

  const fetchPendingMakeupSessions = async () => {
    console.time('🔄 fetchPendingMakeupSessions - Total Time');
    try {
      setPendingMakeupLoading(true);
      
      const endDate = new Date();
      const endDateStr = getLocalDateString(endDate);

      console.time('📊 Supabase - Pending Makeup Sessions Query');
      console.log(`📈 Querying all pending makeup sessions through ${endDateStr}`);

      const selectQuery = `
          *,
          session:sessions!session_id (
            id,
            day,
            time,
            session_type,
            valid_from,
            valid_until,
            is_makeup,
            makeup_for
          ),
          client:clients (
            id,
            name,
            nickname,
            age,
            birthdate,
            gender,
            diagnosis,
            guardian_name,
            guardian_contact,
            pwd,
            pwd_number,
            notes,
            status,
            initial_document,
            given_ca,
            signed_ca,
            signed_cf,
            pwd_card,
            official_receipt,
            or_name,
            client_services (*)
          ),
          therapist:therapists (
            id,
            name,
            specialization
          )
        `;

      // Query 1: Pending makeup sessions (makeup_scheduled = false)
      const { data: pendingMakeupData, error: error1 } = await supabase
        .from('session_attendance')
        .select(selectQuery)
        .eq('makeup_scheduled', false)
        .in('status', ['cancelled', 'absent'])
        .lte('attendance_date', endDateStr);

      // Query 2: Pending no-show fee (paid_noshow = false)
      const { data: pendingNoShowData, error: error2 } = await supabase
        .from('session_attendance')
        .select(selectQuery)
        .eq('paid_noshow', false)
        .in('status', ['cancelled', 'absent'])
        .lte('attendance_date', endDateStr);

      console.timeEnd('📊 Supabase - Pending Makeup Sessions Query');

      if (error1) throw error1;
      if (error2) throw error2;

      // Combine results and remove duplicates (by attendance id)
      const combinedData = [...(pendingMakeupData || []), ...(pendingNoShowData || [])];
      const uniqueData = combinedData.filter((item, index, self) => 
        index === self.findIndex(t => t.id === item.id)
      );

      // Hide pending items for inactive clients (matches ClientManagement Active/Inactive status)
      const visiblePendingData = uniqueData.filter((record) => record.client?.status !== 'Inactive');
      
      // Sort by attendance_date descending
      visiblePendingData.sort((a, b) => new Date(b.attendance_date) - new Date(a.attendance_date));

      console.time('🔄 Data Processing - Pending Makeup Sessions');
      console.log(`📈 Processing ${visiblePendingData.length} pending sessions after active-client filter (${uniqueData.length} raw combined; makeup: ${pendingMakeupData?.length || 0}, no-show: ${pendingNoShowData?.length || 0})`);

      // Process the data to add calculated fields
      const processedSessions = visiblePendingData.map(record => {
        const matchingService = record.client?.client_services?.find(service => {
          const specializationToService = {
            'Speech Therapy': 'ST',
            'Occupational Therapy': 'OT',
            'Physical Therapy': 'PT',
            'Play Group': 'PG'
          };
          const serviceMatches = service.service_type === specializationToService[record.therapist.specialization];
          const dateMatches = (!service.pricing_valid_from || service.pricing_valid_from <= record.attendance_date) &&
                            (!service.pricing_valid_until || service.pricing_valid_until >= record.attendance_date);
          return serviceMatches && dateMatches;
        });

        return {
          ...record.session,
          client: record.client,
          therapist: record.therapist,
          attendance: {
            ...record,
            session: undefined,
            client: undefined,
            therapist: undefined
          },
          base_rate: getClientBillingRate(matchingService, record.attendance_date),
          discounted_rate: calculatePrice(
            getClientBillingRate(matchingService, record.attendance_date),
            record.client?.pwd || false,
            matchingService?.vat_inclusive || false
          ),
          service_type: matchingService?.service_type || 'N/A',
          // Add attendance_date for grouping
          attendance_date: record.attendance_date
        };
      });
      console.timeEnd('🔄 Data Processing - Pending Makeup Sessions');

      setPendingMakeupSessions(processedSessions);

      // Future makeup sessions — query sessions table directly (is_makeup = true, valid_from >= today)
      const todayStr = getLocalDateString(new Date());
      const { data: futureMakeupSessionsData, error: error3 } = await supabase
        .from('sessions')
        .select(`
          id,
          client_id,
          client_name,
          day,
          time,
          session_type,
          notes,
          valid_from,
          valid_until,
          is_makeup,
          makeup_for,
          session_attendance_id,
          client:clients (
            id,
            name,
            nickname,
            status
          ),
          therapist:therapists (
            id,
            name,
            specialization
          )
        `)
        .eq('is_makeup', true)
        .gte('valid_from', todayStr)
        .order('valid_from', { ascending: true });

      if (error3) throw error3;

      const processedScheduledSessions = (futureMakeupSessionsData || [])
        .filter((session) => session.client?.status !== 'Inactive')
        .map((session) => ({
          id: session.id,
          client: session.client || { name: session.client_name },
          therapist: session.therapist,
          originalMakeupDate: session.makeup_for,
          scheduledMakeupDate: session.valid_from,
          scheduledMakeupTime: session.time,
        }));

      setScheduledFutureMakeupSessions(processedScheduledSessions);
    } catch (error) {
      toast({
        title: 'Error fetching pending makeup sessions',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setPendingMakeupLoading(false);
      console.timeEnd('🔄 fetchPendingMakeupSessions - Total Time');
    }
  };

  const fetchNewClientReminderSessions = async (dateOverride) => {
    const fetchId = ++reminderFetchIdRef.current;
    const targetDate = dateOverride ?? selectedDate;

    try {
      setNewClientRemindersLoading(true);

      const dayOfWeek = DAYS[targetDate.getDay()];
      const selectedDateStr = getLocalDateString(targetDate);

      const sessionSelect = `
          id,
          client_id,
          client_name,
          therapist_id,
          day,
          time,
          session_type,
          notes,
          valid_from,
          valid_until,
          is_makeup,
          makeup_for,
          client:clients (
            id,
            name,
            nickname,
            guardian_name,
            guardian_contact,
            diagnosis,
            birthdate,
            age,
            status
          ),
          therapist:therapists (
            id,
            name,
            nickname,
            specialization
          )
        `;

      const sessionFilters = (query) =>
        query
          .eq('day', dayOfWeek)
          .lte('valid_from', selectedDateStr)
          .or(`valid_until.is.null,valid_until.gte.${selectedDateStr}`);

      const [{ data: ieEndorsementData, error: ieError }, { data: makeupData, error: makeupError }] =
        await Promise.all([
          sessionFilters(
            supabase.from('sessions').select(sessionSelect).in('session_type', ['IE', 'Endorsement'])
          ),
          sessionFilters(
            supabase.from('sessions').select(sessionSelect).eq('is_makeup', true)
          ),
        ]);

      if (ieError) throw ieError;
      if (makeupError) throw makeupError;

      const combinedSessions = [...(ieEndorsementData || []), ...(makeupData || [])].filter(
        (session, index, self) => index === self.findIndex((item) => item.id === session.id)
      );

      const activeSessions = combinedSessions.filter(
        (session) => session.client?.status !== 'Inactive'
      );

      const sessionIds = activeSessions.map((session) => session.id);
      let attendedSessionIds = new Set();

      if (sessionIds.length > 0) {
        const { data: attendanceData, error: attendanceError } = await supabase
          .from('session_attendance')
          .select('session_id, status')
          .eq('attendance_date', selectedDateStr)
          .in('session_id', sessionIds)
          .eq('status', 'present');

        if (attendanceError) throw attendanceError;

        attendedSessionIds = new Set((attendanceData || []).map((record) => record.session_id));
      }

      const reminderSessions = activeSessions
        .filter((session) => !attendedSessionIds.has(session.id))
        .sort((a, b) => parseInt(a.time || '0', 10) - parseInt(b.time || '0', 10));

      if (fetchId !== reminderFetchIdRef.current) {
        return;
      }

      setNewClientReminderSessions(reminderSessions);
    } catch (error) {
      if (fetchId !== reminderFetchIdRef.current) {
        return;
      }

      toast({
        title: 'Error fetching schedule reminders',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      if (fetchId === reminderFetchIdRef.current) {
        setNewClientRemindersLoading(false);
      }
    }
  };

  const fetchMakeupOpportunities = async () => {
    try {
      setMakeupOpportunitiesLoading(true);

      const dayOfWeek = DAYS[selectedDate.getDay()];
      const selectedDateStr = getLocalDateString(selectedDate);

      let { data: therapistsData, error: therapistsError } = await supabase
        .from('therapists')
        .select('id, name, specialization, schedule, status')
        .in('status', ['active', 'Active']);

      if (therapistsError?.message?.includes('status')) {
        ({ data: therapistsData, error: therapistsError } = await supabase
          .from('therapists')
          .select('id, name, specialization, schedule'));
      }

      if (therapistsError) throw therapistsError;

      const [
        { data: daySessions, error: sessionsError },
        { data: attendanceRecords, error: attendanceError },
        { data: openAvailability, error: availabilityError },
        { data: pendingMakeupRecords, error: pendingError },
      ] = await Promise.all([
        supabase
          .from('sessions')
          .select('id, therapist_id, client_id, day, time, valid_from, valid_until, is_makeup')
          .eq('day', dayOfWeek)
          .lte('valid_from', selectedDateStr)
          .or(`valid_until.is.null,valid_until.gte.${selectedDateStr}`),
        supabase
          .from('session_attendance')
          .select('session_id, attendance_date, status')
          .eq('attendance_date', selectedDateStr),
        supabase
          .from('makeup_session_availability')
          .select('therapist_id, date, time_slot, is_open')
          .eq('date', selectedDateStr)
          .eq('is_open', true),
        supabase
          .from('session_attendance')
          .select(`
            id,
            client_id,
            therapist_id,
            attendance_date,
            status,
            makeup_scheduled,
            madeup_for,
            paid_noshow,
            client:clients (
              id,
              name,
              nickname,
              status
            ),
            therapist:therapists (
              id,
              name,
              specialization
            )
          `)
          .eq('madeup_for', false)
          .in('status', ['cancelled', 'absent'])
          .lte('attendance_date', selectedDateStr),
      ]);

      if (sessionsError) throw sessionsError;
      if (attendanceError) throw attendanceError;
      if (availabilityError) throw availabilityError;
      if (pendingError) throw pendingError;

      const therapists = therapistsData || [];
      const sessions = daySessions || [];
      const attendanceMap = buildAttendanceMap(attendanceRecords || []);
      const pendingMakeups = (pendingMakeupRecords || []).filter(
        (record) => record.client?.status !== 'Inactive' && isPendingMakeupRecord(record)
      );

      const candidateClientIds = [...new Set(pendingMakeups.map((record) => record.client_id))];
      let clientSessions = sessions;

      if (candidateClientIds.length > 0) {
        const { data: clientDaySessions, error: clientSessionsError } = await supabase
          .from('sessions')
          .select('id, therapist_id, client_id, day, time, valid_from, valid_until, is_makeup')
          .eq('day', dayOfWeek)
          .in('client_id', candidateClientIds)
          .lte('valid_from', selectedDateStr)
          .or(`valid_until.is.null,valid_until.gte.${selectedDateStr}`);

        if (clientSessionsError) throw clientSessionsError;

        const mergedById = new Map();
        [...sessions, ...(clientDaySessions || [])].forEach((session) => {
          mergedById.set(session.id, session);
        });
        clientSessions = [...mergedById.values()];
      }

      const availableSlots = filterFutureSlotsForToday(
        buildAvailableTherapistSlots({
          dateStr: selectedDateStr,
          dayName: dayOfWeek,
          therapists,
          sessions,
          attendanceMap,
          openAvailability: openAvailability || [],
        }),
        selectedDateStr
      );

      const opportunities = buildMakeupOpportunities({
        dateStr: selectedDateStr,
        dayName: dayOfWeek,
        availableSlots,
        pendingMakeups,
        clientSessions,
        attendanceMap,
        therapists,
      });

      setMakeupOpportunities(opportunities);
    } catch (error) {
      toast({
        title: 'Error fetching makeup opportunities',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setMakeupOpportunities([]);
    } finally {
      setMakeupOpportunitiesLoading(false);
    }
  };

  useEffect(() => {
    // Listen for custom refresh events from SessionCard payment buttons
    const handleRefreshUnpaidSessions = () => {
      if (view === 'unpaid') {
        fetchUnpaidSessions();
      }
    };
  
    window.addEventListener('refreshUnpaidSessions', handleRefreshUnpaidSessions);
  
    return () => {
      window.removeEventListener('refreshUnpaidSessions', handleRefreshUnpaidSessions);
    };
  }, [view, fetchUnpaidSessions]);

  // Fetch available makeup slots for the selected session's therapist
  const fetchAvailableMakeupSlots = useCallback(async (therapistId) => {
    if (!therapistId) return;
    
    setFetchingMakeupSlots(true);
    try {
      const today = new Date();
      const startDate = getLocalDateString(today);
      const endDate = new Date(today);
      endDate.setDate(endDate.getDate() + 14); // Next 14 days
      const endDateStr = getLocalDateString(endDate);

      // Fetch open slots from makeup_session_availability
      const { data, error } = await supabase
        .from('makeup_session_availability')
        .select('*')
        .eq('therapist_id', therapistId)
        .eq('is_open', true)
        .gte('date', startDate)
        .lte('date', endDateStr)
        .order('date', { ascending: true })
        .order('time_slot', { ascending: true });

      if (error) throw error;

      // Filter out past time slots for today
      const now = new Date();
      const filteredSlots = data?.filter(slot => {
        const slotDate = new Date(slot.date);
        if (getLocalDateString(slotDate) === getLocalDateString(now)) {
          const [hours] = slot.time_slot.split(':').map(Number);
          return hours > now.getHours();
        }
        return true;
      }) || [];

      // Check for existing sessions at these times to avoid conflicts.
      // Cancelled/absent attendance frees the slot (therapist may have re-opened it for makeup).
      const slotDates = [...new Set(filteredSlots.map(s => s.date))];

      const normalizeTimeSlot = (time) => {
        const hour = parseInt(String(time).split(':')[0], 10);
        return Number.isNaN(hour) ? String(time) : `${hour}:00`;
      };

      if (slotDates.length > 0) {
        const { data: existingSessions, error: sessionsError } = await supabase
          .from('sessions')
          .select('id, day, time, valid_from, valid_until')
          .eq('therapist_id', therapistId)
          .lte('valid_from', endDateStr)
          .or(`valid_until.is.null,valid_until.gte.${startDate}`);

        if (!sessionsError && existingSessions) {
          const sessionIds = existingSessions.map((s) => s.id);
          const attendanceBySessionAndDate = {};

          if (sessionIds.length > 0) {
            const { data: attendanceRecords, error: attendanceError } = await supabase
              .from('session_attendance')
              .select('session_id, attendance_date, status')
              .eq('therapist_id', therapistId)
              .in('session_id', sessionIds)
              .in('attendance_date', slotDates);

            if (!attendanceError && attendanceRecords) {
              attendanceRecords.forEach((record) => {
                attendanceBySessionAndDate[`${record.session_id}_${record.attendance_date}`] =
                  record.status;
              });
            }
          }

          const sessionOccupiesSlot = (session, slotDate, slotDay, slotTime) => {
            const sessionValidUntil = session.valid_until || '9999-12-31';
            if (slotDate < session.valid_from || slotDate > sessionValidUntil) return false;
            if (session.day !== slotDay) return false;
            if (normalizeTimeSlot(session.time) !== normalizeTimeSlot(slotTime)) return false;

            const status = attendanceBySessionAndDate[`${session.id}_${slotDate}`];
            if (!status) return true;
            return !isCancelledOrLateCancellationStatus(status);
          };

          const availableSlots = filteredSlots.filter((slot) => {
            const slotDay = DAYS[new Date(slot.date).getDay()];
            const hasConflict = existingSessions.some((session) =>
              sessionOccupiesSlot(session, slot.date, slotDay, slot.time_slot)
            );
            return !hasConflict;
          });

          setAvailableMakeupSlots(sortMakeupAvailabilitySlots(availableSlots));
        } else {
          setAvailableMakeupSlots(sortMakeupAvailabilitySlots(filteredSlots));
        }
      } else {
        setAvailableMakeupSlots([]);
      }
    } catch (error) {
      console.error('Error fetching makeup slots:', error);
      toast({
        title: 'Error loading available makeup slots',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setAvailableMakeupSlots([]);
    } finally {
      setFetchingMakeupSlots(false);
    }
  }, [DAYS, toast]);

  // Fetch makeup slots when attendance modal opens with cancelled/absent status
  useEffect(() => {
    const isCancelledOrAbsent =
      attendanceForm.status === 'absent' || attendanceForm.status === 'cancelled';

    const shouldFetch =
      isAttendanceModalOpen &&
      selectedSession &&
      isCancelledOrAbsent &&
      !paidNoShowFee &&
      !cancelledMakeupByTherapist &&
      ((!selectedSession.is_makeup && !attendanceForm.noMakeupRequired) ||
        (selectedSession.is_makeup && rescheduleMakeup));

    if (shouldFetch) {
      fetchAvailableMakeupSlots(rescheduleTherapistId || selectedSession.therapist.id);
    } else {
      setAvailableMakeupSlots([]);
      if (!shouldFetch) {
        setSelectedMakeupSlot(null);
      }
    }
  }, [
    isAttendanceModalOpen,
    selectedSession,
    attendanceForm.status,
    attendanceForm.noMakeupRequired,
    paidNoShowFee,
    cancelledMakeupByTherapist,
    rescheduleMakeup,
    rescheduleTherapistId,
    fetchAvailableMakeupSlots,
  ]);

  useEffect(() => {
    const loadRescheduleTherapists = async () => {
      if (
        !isAttendanceModalOpen ||
        !rescheduleMakeup ||
        !selectedSession?.therapist?.specialization
      ) {
        return;
      }

      const currentId = selectedSession.therapist.id;
      if (!rescheduleTherapistId) {
        setRescheduleTherapistId(currentId);
      }

      try {
        let { data, error } = await supabase
          .from('therapists')
          .select('id, name, specialization, status')
          .eq('specialization', selectedSession.therapist.specialization)
          .in('status', ['active', 'Active'])
          .order('name');

        if (error?.message?.includes('status')) {
          ({ data, error } = await supabase
            .from('therapists')
            .select('id, name, specialization')
            .eq('specialization', selectedSession.therapist.specialization)
            .order('name'));
        }

        if (error) throw error;

        const options = data || [];
        if (!options.some((therapist) => therapist.id === currentId)) {
          options.unshift({
            id: currentId,
            name: selectedSession.therapist.name,
            specialization: selectedSession.therapist.specialization,
          });
        }
        setRescheduleTherapistOptions(options);
      } catch (error) {
        console.error('Error loading therapists for makeup reschedule:', error);
        setRescheduleTherapistOptions([
          {
            id: currentId,
            name: selectedSession.therapist.name,
            specialization: selectedSession.therapist.specialization,
          },
        ]);
      }
    };

    loadRescheduleTherapists();
  }, [
    isAttendanceModalOpen,
    rescheduleMakeup,
    rescheduleTherapistId,
    selectedSession,
  ]);

  useEffect(() => {
    const fetchGraceCancellationCount = async () => {
      if (
        !isAttendanceModalOpen ||
        !selectedSession?.client?.id ||
        attendanceForm.status !== 'absent'
      ) {
        return;
      }

      const year = new Date(
        selectedSession.attendance_date ||
          selectedSession.attendance?.attendance_date ||
          selectedDate
      ).getFullYear();

      try {
        const { data, error } = await supabase
          .from('session_attendance')
          .select('id, grace_cancellation, attendance_date')
          .eq('client_id', selectedSession.client.id)
          .eq('grace_cancellation', true);

        if (error) throw error;

        const currentId = selectedSession.attendance?.id;
        const filtered = (data || []).filter((row) => row.id !== currentId);
        setGraceCancellationUsedCount(countGraceCancellationsForYear(filtered, year));
      } catch (error) {
        console.error('Error fetching grace cancellation count:', error);
        setGraceCancellationUsedCount(0);
      }
    };

    fetchGraceCancellationCount();
  }, [
    isAttendanceModalOpen,
    selectedSession,
    attendanceForm.status,
    selectedDate,
  ]);

  useEffect(() => {
    const fetchExistingLinkedMakeup = async () => {
      if (
        !isAttendanceModalOpen ||
        !selectedSession?.attendance?.makeup_scheduled ||
        !selectedSession?.attendance?.id
      ) {
        setExistingLinkedMakeup(null);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('sessions')
          .select('valid_from, time')
          .eq('session_attendance_id', selectedSession.attendance.id)
          .eq('is_makeup', true)
          .maybeSingle();

        if (error) throw error;
        setExistingLinkedMakeup(
          data ? { date: data.valid_from, time: data.time } : null
        );
      } catch (error) {
        console.error('Error fetching linked makeup session:', error);
        setExistingLinkedMakeup(null);
      }
    };

    fetchExistingLinkedMakeup();
  }, [isAttendanceModalOpen, selectedSession]);

  // Reset makeup-related and document states when modal closes
  useEffect(() => {
    if (!isAttendanceModalOpen) {
      setSelectedMakeupSlot(null);
      setExistingLinkedMakeup(null);
      setPaidNoShowFee(false);
      setCancelledMakeupByTherapist(false);
      setRescheduleMakeup(false);
      setRescheduleTherapistId('');
      setRescheduleTherapistOptions([]);
      setGraceCancellation(false);
      setWaivedLateCancellation(false);
      setGraceCancellationUsedCount(0);
      setAvailableMakeupSlots([]);
      setDocumentStatus(DEFAULT_DOCUMENT_STATUS);
    }
  }, [isAttendanceModalOpen]);
  
  const handleAddOneTimeSession = async () => {
    try {
      const selectedDateStr = getLocalDateString(selectedDate);
      
      // Convert the time to 24-hour format
      const timeStr = oneTimeSessionForm.time;
      const hour = parseInt(timeStr);
      const formattedTime = `${hour}:00`;
  
      // Get client name from availableClients
      const selectedClient = availableClients.find(client => client.id === oneTimeSessionForm.client_id);
      const clientName = selectedClient ? selectedClient.name : '';
      
      // Check if this is a makeup session
      const isMakeupSession = !!oneTimeSessionForm.pending_makeup_id;
      let makeupForDate = null;
      let sessionAttendanceId = null;
      
      if (isMakeupSession) {
        // Find the selected pending makeup to get the original cancelled date
        const selectedMakeup = clientPendingMakeups.find(
          m => m.id === oneTimeSessionForm.pending_makeup_id
        );
        if (selectedMakeup) {
          makeupForDate = selectedMakeup.attendance_date;
          sessionAttendanceId = selectedMakeup.id;
        }
      }
      
      // Build session data
      const sessionData = {
        therapist_id: oneTimeSessionForm.therapist_id,
        client_id: oneTimeSessionForm.client_id,
        client_name: clientName,
        day: DAYS[selectedDate.getDay()],
        time: formattedTime,
        session_type: 'Regular',
        valid_from: selectedDateStr,
        valid_until: selectedDateStr,
        notes: isMakeupSession 
          ? `Makeup for cancelled session (${new Date(makeupForDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })})${oneTimeSessionForm.notes ? ' - ' + oneTimeSessionForm.notes : ''}`
          : oneTimeSessionForm.notes,
        // Makeup session fields
        is_makeup: isMakeupSession || null,
        makeup_for: makeupForDate,
        session_attendance_id: sessionAttendanceId
      };

      // Insert the session
      const { data, error } = await supabase
        .from('sessions')
        .insert([sessionData])
        .select();
  
      if (error) throw error;
  
      console.log('Inserted session:', data);

      // If this is a makeup session, update the original attendance record
      if (isMakeupSession && sessionAttendanceId) {
        const { error: updateError } = await supabase
            .from('session_attendance')
            .update({ makeup_scheduled: true, voided_linked_makeup_on: null })
            .eq('id', sessionAttendanceId);

        if (updateError) {
          console.error('Error updating makeup_scheduled:', updateError);
          // Don't throw - session was created successfully
        }

        toast({
          title: 'Makeup session scheduled',
          description: `Makeup session added for ${new Date(makeupForDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
          status: 'success',
          duration: 3000
        });
      } else {
        toast({
          title: 'Session added successfully',
          status: 'success',
          duration: 3000
        });
      }
  
      setIsOneTimeModalOpen(false);
      setOneTimeSessionForm({
        therapist_id: '',
        client_id: '',
        time: '',
        notes: '',
        pending_makeup_id: ''
      });
      
      // Refresh the sessions list and pending makeup if in that view
      await fetchSessions();
      if (view === 'pending-makeup') {
        await fetchPendingMakeupSessions();
      }
      if (view === 'makeup-opportunities') {
        await fetchMakeupOpportunities();
      }
    } catch (error) {
      console.error('Error details:', error);
      toast({
        title: 'Error adding session',
        description: error.message,
        status: 'error',
        duration: 5000
      });
    }
  };

  const getPackageRate = async (serviceType, isPwd, isVatInclusive) => {
    try {
      const currentDate = getLocalDateString(new Date());
      
      const { data, error } = await supabase
        .from('service_rates')
        .select('*')
        .eq('service_type', serviceType)
        .eq('session_type', 'Regular')
        .eq('is_package', true)
        .lte('valid_from', currentDate)
        .or(`valid_until.is.null,valid_until.gte.${currentDate}`)
        .single();
  
      if (error) throw error;

      console.log('Data: ', data)
      console.log('Is VAT Inclusive: ', isVatInclusive)
  
      if (!data) return null;
  
      if (isVatInclusive) {
        console.log('ENTERED')
        const VAT_RATE = 0.12;
        if (isPwd) {
          const preVatRate = data.base_rate / (1 + VAT_RATE);
          const discountedPreVatRate = preVatRate * 0.8;
          return Math.round((discountedPreVatRate + (data.base_rate-preVatRate)) * 100) / 100;
        }
        return data.base_rate;
      } else {
        return isPwd ? data.base_rate * 0.8 : data.base_rate;
      }
    } catch (error) {
      console.error('Error fetching package rate:', error);
      return null;
    }
  };

  // SOLUTION: Remove the expensive client_services join and fetch only when needed
  // Replace your fetchSessions function with this optimized version:

  const fetchSessions = useCallback(async () => {
    if (!isVisible) {
      console.log('🔇 fetchSessions skipped - not visible');
      return;
    }
    console.time('🔄 fetchSessions - Total Time');
    try {
      setIsLoading(true);
      const dayOfWeek = DAYS[selectedDate.getDay()];
      const selectedDateStr = getLocalDateString(selectedDate);

      console.time('📊 Supabase - Attendance Query (Optimized)');
      // Query 1: Get attendance records (unchanged - this is fast now)
      const { data: attendanceData, error: attendanceError } = await supabase
        .from('session_attendance')
        .select(`
          id,
          session_id,
          client_id,
          therapist_id,
          attendance_date,
          status,
          notes,
          base_rate,
          pwd_discount,
          final_amount,
          session_type,
          billing_service_type,
          promo_session,
          amount_paid,
          or_number,
          center_fee,
          therapist_fee,
          center_or_number,
          therapist_or_number,
          payment_date,
          is_paid,
          madeup_for,
          paid_noshow,
          makeup_scheduled,
          grace_cancellation,
          waived_late_cancellation,
          voided_linked_makeup_on,
          makeup_fee_voided_by_therapist
        `)
        .eq('attendance_date', selectedDateStr);
      console.timeEnd('📊 Supabase - Attendance Query (Optimized)');

      if (attendanceError) throw attendanceError;

      console.time('📊 Supabase - Sessions Query (Optimized)');
      // Query 2: Get sessions (unchanged - this is fast now)
      const { data: sessionsData, error: sessionsError } = await supabase
        .from('sessions')
        .select(`
          id,
          therapist_id,
          client_id,
          client_name,
          day,
          time,
          session_type,
          billing_service_type,
          notes,
          valid_from,
          valid_until,
          is_makeup,
          makeup_for,
          session_attendance_id
        `)
        .eq('day', dayOfWeek)
        .lte('valid_from', selectedDateStr)
        .or(`valid_until.is.null,valid_until.gte.${selectedDateStr}`);
      console.timeEnd('📊 Supabase - Sessions Query (Optimized)');

      if (sessionsError) throw sessionsError;

      // Get unique client and therapist IDs
      const clientIds = [...new Set([
        ...(attendanceData?.map(a => a.client_id) || []),
        ...(sessionsData?.map(s => s.client_id) || [])
      ])];
      
      const therapistIds = [...new Set([
        ...(attendanceData?.map(a => a.therapist_id) || []),
        ...(sessionsData?.map(s => s.therapist_id) || [])
      ])];

      console.time('📊 Supabase - Clients Query (NO client_services join)');
      // Query 3: Fetch clients WITHOUT the expensive client_services join
      const { data: clientsData, error: clientsError } = await supabase
        .from('clients')
        .select(`
          id,
          name,
          nickname,
          age,
          birthdate,
          gender,
          diagnosis,
          guardian_name,
          guardian_contact,
          pwd,
          pwd_number,
          notes,
          initial_document,
          given_ca,
          signed_ca,
          signed_cf,
          pwd_card,
          official_receipt,
          or_name
        `)
        .in('id', clientIds);
      console.timeEnd('📊 Supabase - Clients Query (NO client_services join)');

      if (clientsError) throw clientsError;

      console.time('📊 Supabase - Therapists Query (Bulk)');
      // Query 4: Bulk fetch therapists (unchanged)
      const { data: therapistsData, error: therapistsError } = await supabase
        .from('therapists')
        .select(`
          id,
          name,
          specialization,
          feeding_therapy_certified,
          sworn_declaration
        `)
        .in('id', therapistIds);
      console.timeEnd('📊 Supabase - Therapists Query (Bulk)');

      if (therapistsError) throw therapistsError;

      console.time('📊 Supabase - Client Services Query (Targeted)');
      // Query 5: Fetch ONLY the client_services we actually need for pricing
      const { data: clientServicesData, error: clientServicesError } = await supabase
        .from('client_services')
        .select(`
          client_id,
          service_type,
          base_rate,
          vat_inclusive,
          sunday_rate,
          sunday_regular_rate,
          reservation_fee,
          promo_count,
          pricing_valid_from,
          pricing_valid_until
        `)
        .in('client_id', clientIds)
        .lte('pricing_valid_from', selectedDateStr)
        .or(`pricing_valid_until.is.null,pricing_valid_until.gte.${selectedDateStr}`);
      console.timeEnd('📊 Supabase - Client Services Query (Targeted)');

      if (clientServicesError) throw clientServicesError;

      console.time('🔄 Data Processing - Create Lookup Maps');
      // Create lookup maps for O(1) access
      const clientsMap = new Map(clientsData?.map(c => [c.id, c]) || []);
      const therapistsMap = new Map(therapistsData?.map(t => [t.id, t]) || []);
      
      // Create client services map grouped by client_id
      const clientServicesMap = new Map();
      clientServicesData?.forEach(service => {
        if (!clientServicesMap.has(service.client_id)) {
          clientServicesMap.set(service.client_id, []);
        }
        clientServicesMap.get(service.client_id).push(service);
      });
      console.timeEnd('🔄 Data Processing - Create Lookup Maps');

      console.time('🔄 Data Processing - Process Attendance');
      console.log(`📈 Processing ${attendanceData?.length || 0} attendance records`);
      
      // Process attendance records
      const attendanceSessions = attendanceData?.map(record => {
        const client = clientsMap.get(record.client_id);
        const therapist = therapistsMap.get(record.therapist_id);
        const session = sessionsData?.find(s => s.id === record.session_id);
        
        if (!client || !therapist) return null;

        // Add client_services to client object for compatibility
        client.client_services = clientServicesMap.get(client.id) || [];

        const billingServiceType = resolveBillingServiceType(
          { billing_service_type: record.billing_service_type, session },
          therapist,
          client.client_services,
          selectedDateStr
        );
        const matchingService = findMatchingClientService(
          client.client_services,
          therapist.specialization,
          selectedDateStr,
          billingServiceType
        );
      
        const clientRate = getClientBillingRate(
          matchingService,
          getBillingRateDate(selectedDateStr, session)
        );

        return {
          ...session,
          client,
          therapist,
          attendance: record,
          billing_service_type: billingServiceType,
          base_rate: clientRate,
          discounted_rate: calculatePrice(
            clientRate,
            client?.pwd || false,
            matchingService?.vat_inclusive || false
          ),
          service_type: billingServiceType
        };
      }).filter(Boolean) || [];
      console.timeEnd('🔄 Data Processing - Process Attendance');

      console.time('🔄 Data Processing - Process Scheduled Sessions');
      console.log(`📈 Processing ${sessionsData?.length || 0} scheduled sessions`);
      
      // Process scheduled sessions that don't have attendance records
      const scheduledSessionsProcessed = sessionsData?.map(session => {
        // Skip if we already have an attendance record for this session
        if (attendanceSessions.some(as => as.id === session.id)) {
          return null;
        }
      
        const client = clientsMap.get(session.client_id);
        const therapist = therapistsMap.get(session.therapist_id);
        
        if (!client || !therapist) return null;

        // Add client_services to client object for compatibility
        client.client_services = clientServicesMap.get(client.id) || [];

        const billingServiceType = resolveBillingServiceType(
          session,
          therapist,
          client.client_services,
          selectedDateStr
        );
        const matchingService = findMatchingClientService(
          client.client_services,
          therapist.specialization,
          selectedDateStr,
          billingServiceType
        );
      
        const clientRate = getClientBillingRate(
          matchingService,
          getBillingRateDate(selectedDateStr, session)
        );

        return {
          ...session,
          client,
          therapist,
          billing_service_type: billingServiceType,
          base_rate: clientRate,
          discounted_rate: calculatePrice(
            clientRate,
            client?.pwd || false,
            matchingService?.vat_inclusive || false
          ),
          service_type: billingServiceType
        };
      }).filter(Boolean) || [];
      console.timeEnd('🔄 Data Processing - Process Scheduled Sessions');

      console.time('🔄 Data Processing - Combining Sessions');
      // Combine both arrays
      const combinedSessions = [...attendanceSessions, ...scheduledSessionsProcessed];
      console.log(`📈 Final combined sessions: ${combinedSessions.length}`);
      console.timeEnd('🔄 Data Processing - Combining Sessions');

      setSessions(combinedSessions);
    } catch (error) {
      console.error('❌ Error in fetchSessions:', error);
      toast({
        title: 'Error fetching sessions',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
      console.timeEnd('🔄 fetchSessions - Total Time');
    }
  }, [selectedDate, DAYS, calculatePrice, toast, isVisible]);

  const handleRefresh = useCallback(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleAttendanceSubmit = async () => {
    try {
        setIsSubmittingAttendance(true);
        // IMPORTANT: Fetch fresh client services data to get the latest promo count
        const { data: freshClientServices, error: clientServicesError } = await supabase
            .from('client_services')
            .select(`
                client_id,
                service_type,
                base_rate,
                vat_inclusive,
                sunday_rate,
                sunday_regular_rate,
                reservation_fee,
                promo_count,
                pricing_valid_from,
                pricing_valid_until
            `)
            .eq('client_id', selectedSession.client.id)
            .lte('pricing_valid_from', getLocalDateString(selectedDate))
            .or(`pricing_valid_until.is.null,pricing_valid_until.gte.${getLocalDateString(selectedDate)}`);

        if (clientServicesError) throw clientServicesError;

        // Use fresh data instead of cached data
        const billingServiceType = resolveBillingServiceType(
          selectedSession,
          selectedSession.therapist,
          freshClientServices,
          getLocalDateString(selectedDate)
        );
        const matchingService = findMatchingClientService(
          freshClientServices,
          selectedSession.therapist.specialization,
          getLocalDateString(selectedDate),
          billingServiceType
        );

        console.log('Fresh Matching Service: ', matchingService);

        console.log('Selected Session: ', selectedSession);
  
        // Check if attendance record already exists
        // Use the session's attendance_date if available (from Pending Makeup/Unpaid tabs)
        // Or check the nested attendance object (from Client Schedule tab)
        // Otherwise use the currently selected date
        const attendanceDateToCheck = selectedSession.attendance_date 
            || selectedSession.attendance?.attendance_date 
            || getLocalDateString(selectedDate);
        
        console.log("Session attendance_date:", selectedSession.attendance_date);
        console.log("Session attendance?.attendance_date:", selectedSession.attendance?.attendance_date);
        console.log("selectedDate:", getLocalDateString(selectedDate));
        
        const { data: existingAttendance } = await supabase
            .from('session_attendance')
            .select('*')  // Changed from just 'id' to get full record
            .eq('session_id', selectedSession.id)
            .eq('attendance_date', attendanceDateToCheck)
            .single();

        console.log("Existing Attendance: ", existingAttendance);
        console.log("Attendance date checked: ", attendanceDateToCheck);

        // Determine if this is an initial session by checking either:
        // 1. For a new record: check the original session type
        // 2. For existing record: check the stored session_type in attendance
        const isInitialSession = existingAttendance 
            ? ['IE', 'Endorsement'].includes(existingAttendance.session_type)
            : ['IE', 'Endorsement'].includes(selectedSession.session_type);

        console.log("Is Initial Session: ", isInitialSession);

        const sessionType = existingAttendance?.session_type ?? selectedSession.session_type;
        const feeApplies = await shouldApplyReservationFee({
          clientId: selectedSession.client.id,
          sessionType,
          therapistId: selectedSession.therapist.id,
          therapistSpecialization: selectedSession.therapist.specialization,
          excludeAttendanceId: existingAttendance?.id ?? null,
        });
        const reservationFeeDeduction = getReservationFeeDeduction(matchingService, feeApplies);

        // Form shows net amount due; store gross (net + reservation fee) when fee applies
        let dbAmountPaid = parseFloat(attendanceForm.amountPaid) || 0;
        if (isInitialSession && attendanceForm.status === 'present' && feeApplies) {
          dbAmountPaid = netToGrossAmountPaid(dbAmountPaid, reservationFeeDeduction);
        }

        let newPromoCount = null;
        if (!existingAttendance) {
            // If this is a promo session start or within promo period
            if ((attendanceForm.isPromo || matchingService?.promo_count > 0) && 
                attendanceForm.status === 'present') { 
                // Update promo_count in client_services - use FRESH data
                newPromoCount = matchingService?.promo_count ? matchingService.promo_count + 1 : 1;
            }
        } else { // If editing an existing session_attendance, promo count shouldn't move
            if ((attendanceForm.isPromo || matchingService?.promo_count > 0) && 
                attendanceForm.status === 'present') {
                newPromoCount = matchingService?.promo_count ? matchingService.promo_count : 1;
            }
        }

        const isSupposedMakeup = isSupposedMakeupSession(selectedSession);

        const waiveMakeupByTherapist =
          isSupposedMakeup &&
          (attendanceForm.status === 'cancelled' ||
            attendanceForm.status === 'absent') &&
          cancelledMakeupByTherapist;

        const isReschedulingCancelledMakeup =
          isSupposedMakeup &&
          (attendanceForm.status === 'cancelled' ||
            attendanceForm.status === 'absent') &&
          rescheduleMakeup &&
          Boolean(selectedMakeupSlot);

        // Determine madeup_for value based on options selected
        // Note: use null (not undefined) when clearing — undefined keys are omitted by the client
        // and would leave stale madeup_for / makeup_scheduled from a prior cancellation.
        let madeupForValue = undefined;
        let makeupScheduledValue = undefined;
        if (
          attendanceForm.status === 'cancelled' ||
          attendanceForm.status === 'absent'
        ) {
          if (
            attendanceForm.noMakeupRequired ||
            paidNoShowFee ||
            waiveMakeupByTherapist
          ) {
            madeupForValue = null; // No makeup required or bypassed with fee / therapist waive
            makeupScheduledValue = null;
          } else if (selectedMakeupSlot) {
            madeupForValue = selectedSession?.is_makeup ? null : false;
            makeupScheduledValue = selectedSession?.is_makeup ? null : true;
          } else if (existingAttendance?.makeup_scheduled === true) {
            madeupForValue = existingAttendance.madeup_for;
            makeupScheduledValue = true;
          } else {
            madeupForValue = false;
            makeupScheduledValue = false;
          }
        } else if (attendanceForm.status === 'present') {
          madeupForValue = null;
          makeupScheduledValue = null;
        }

        // Calculate final_amount and is_paid based on status and options
        let finalAmount = 0;
        let isPaidValue = attendanceForm.isPaid;
        let amountPaidValue = attendanceForm.status === 'present' ? dbAmountPaid : null;

        if (attendanceForm.status === 'absent') {
          if (graceCancellation) {
            const { allowed } = canApplyGraceCancellation(graceCancellationUsedCount, true);
            if (!allowed) {
              toast({
                title: 'Grace cancellation limit reached',
                description: `This client has already used all ${GRACE_CANCELLATIONS_PER_YEAR} grace cancellations this year.`,
                status: 'error',
                duration: 5000,
                isClosable: true,
              });
              setIsSubmittingAttendance(false);
              return;
            }
            finalAmount = 0;
            isPaidValue = false;
            amountPaidValue = null;
          } else if (waivedLateCancellation) {
            finalAmount = 0;
            isPaidValue = false;
            amountPaidValue = null;
          } else if (waiveMakeupByTherapist) {
            finalAmount = 0;
            isPaidValue = false;
            amountPaidValue = null;
          } else if (isReschedulingCancelledMakeup) {
            finalAmount = 0;
            isPaidValue = false;
            amountPaidValue = null;
          } else {
            finalAmount = paidNoShowFee ? 0 : 950;
            isPaidValue = paidNoShowFee ? true : false;
            amountPaidValue = paidNoShowFee ? 950 : null;
          }
        } else if (attendanceForm.status === 'cancelled') {
          if (waiveMakeupByTherapist) {
            finalAmount = 0;
            isPaidValue = false;
            amountPaidValue = null;
          } else if (isReschedulingCancelledMakeup) {
            finalAmount = 0;
            isPaidValue = false;
            amountPaidValue = null;
          } else if (paidNoShowFee) {
            finalAmount = 0;
            isPaidValue = true;
            amountPaidValue = 950;
          }
        }

        const sessionDateStr = attendanceDateToCheck;
        const useSplitReceipts = shouldUseSplitReceipts(selectedSession.therapist, sessionDateStr);
        let centerFee = null;
        let therapistFeeAmount = null;
        let centerOrNumber = null;
        let therapistOrNumber = null;
        let orNumberValue = attendanceForm.status === 'present' ? attendanceForm.orNumber : null;

        if (attendanceForm.status === 'present' && useSplitReceipts && documentStatus.official_receipt) {
          if (selectedSession.session_type === 'IE') {
            therapistFeeAmount = 2200;
          } else {
            therapistFeeAmount = await getSessionServiceFeeAsync({
              session: {
                ...selectedSession,
                billing_service_type: billingServiceType,
                is_makeup: selectedSession.is_makeup,
                makeup_for: selectedSession.makeup_for,
              },
              therapistSpecialization: selectedSession.therapist.specialization,
              therapistId: selectedSession.therapist.id,
              date: sessionDateStr,
            }) ?? 0;
          }

          const splitPreview = await getPromoAwareReceiptSplit({
            selectedSession,
            matchingService,
            attendanceForm,
            sessionDateStr,
            totalDue: dbAmountPaid,
            therapistFee: therapistFeeAmount,
            billingServiceType,
          });

          centerFee = splitPreview.therapistReceiptOnly ? null : splitPreview.centerFee;
          therapistFeeAmount = splitPreview.therapistFee;
          centerOrNumber = attendanceForm.centerOrNumber || null;
          therapistOrNumber = attendanceForm.therapistOrNumber || null;
          orNumberValue = centerOrNumber;
        }

        const attendanceData = {
            session_id: selectedSession.id,
            client_id: selectedSession.client.id,
            therapist_id: selectedSession.therapist.id,
            attendance_date: attendanceDateToCheck, // Use the correct date (from session or selectedDate)
            status: attendanceForm.status,
            notes: attendanceForm.notes,
            base_rate: selectedSession.base_rate,
            pwd_discount: selectedSession.client.pwd,
            final_amount: finalAmount,
            session_type: existingAttendance ? existingAttendance.session_type : selectedSession.session_type, // Store original session type
            billing_service_type: attendanceForm.status === 'present' ? billingServiceType : null,
            promo_session: attendanceForm.isPromo ? newPromoCount : null,
            amount_paid: amountPaidValue,
            or_number: orNumberValue,
            center_fee: attendanceForm.status === 'present' && useSplitReceipts && documentStatus.official_receipt ? centerFee : null,
            therapist_fee: attendanceForm.status === 'present' && useSplitReceipts && documentStatus.official_receipt ? therapistFeeAmount : null,
            center_or_number: attendanceForm.status === 'present' && useSplitReceipts && documentStatus.official_receipt ? centerOrNumber : null,
            therapist_or_number: attendanceForm.status === 'present' && useSplitReceipts && documentStatus.official_receipt ? therapistOrNumber : null,
            payment_date:
              (attendanceForm.status === 'present' && attendanceForm.isPaid) || paidNoShowFee
                ? new Date().toISOString()
                : null,
            is_paid: attendanceForm.status === 'present' ? isPaidValue : (paidNoShowFee ? true : false),
            madeup_for: madeupForValue,
            // paid_noshow: true = fee paid, false = fee pending (for cancelled makeup sessions), null = not applicable
            paid_noshow:
                attendanceForm.status === 'present'
                    ? null
                    : paidNoShowFee
                      ? true
                      : waiveMakeupByTherapist || isReschedulingCancelledMakeup
                        ? null
                        : isSupposedMakeup &&
                            (attendanceForm.status === 'cancelled' ||
                              attendanceForm.status === 'absent')
                          ? false
                          : null,
            makeup_scheduled: makeupScheduledValue,
            makeup_fee_voided_by_therapist:
              attendanceForm.status === 'present'
                ? null
                : isSupposedMakeup &&
                  (attendanceForm.status === 'cancelled' ||
                    attendanceForm.status === 'absent')
                  ? Boolean(cancelledMakeupByTherapist)
                  : null,
            grace_cancellation:
              attendanceForm.status === 'absent' ? Boolean(graceCancellation) : false,
            waived_late_cancellation:
              attendanceForm.status === 'absent' ? Boolean(waivedLateCancellation) : false,
            // Clear prior "supposed makeup voided" audit when scheduling a NEW makeup from the original cancelled row
            ...(selectedSession?.is_makeup !== true && makeupScheduledValue === true
              ? { voided_linked_makeup_on: null }
              : {}),
        };
  
        let error;
        let attendanceId;
        if (existingAttendance) {
            const { error: updateError } = await supabase
                .from('session_attendance')
                .update(attendanceData)
                .eq('id', existingAttendance.id);
            error = updateError;
            attendanceId = existingAttendance.id;
        } else {
            const { data: insertedAttendance, error: insertError } = await supabase
                .from('session_attendance')
                .insert([attendanceData]);
            error = insertError;
            attendanceId = insertedAttendance?.id;
        }
  
        if (error) throw error;

        // Get the actual attendance ID if it was inserted (handle Supabase response structure)
        if (!attendanceId && !existingAttendance) {
            // Fetch the just-created attendance record to get its ID
            const { data: newAttendance } = await supabase
                .from('session_attendance')
                .select('id')
                .eq('session_id', selectedSession.id)
                .eq('attendance_date', attendanceDateToCheck)
                .single();
            attendanceId = newAttendance?.id;
        }

        if (
          attendanceData.status === 'present' &&
          attendanceData.session_type === 'IE' &&
          attendanceId
        ) {
          try {
            await upsertInitialEvaluationFromAttendance(supabase, {
              attendanceId,
              clientId: selectedSession.client.id,
              therapistId: selectedSession.therapist.id,
              serviceType: billingServiceType,
              conductedAt: attendanceDateToCheck,
            });
          } catch (ieTrackError) {
            console.error('Error tracking initial evaluation submission:', ieTrackError);
          }
        }

        // Supposed makeup day cancelled by therapist (no fee): clear requirement on original + audit
        if (waiveMakeupByTherapist && selectedSession.session_attendance_id) {
          const originalAttId = selectedSession.session_attendance_id;
          const { error: voidOriginalErr } = await supabase
            .from('session_attendance')
            .update({
              madeup_for: null,
              makeup_scheduled: null,
              voided_linked_makeup_on: attendanceDateToCheck,
            })
            .eq('id', originalAttId);

          if (voidOriginalErr) {
            console.error('Error voiding makeup link on original session:', voidOriginalErr);
            toast({
              title: 'Attendance saved',
              description: `Original session linkage may need manual cleanup: ${voidOriginalErr.message}. If this persists, ensure columns voided_linked_makeup_on and makeup_fee_voided_by_therapist exist on session_attendance (see docs/supabase_add_makeup_void_columns.sql).`,
              status: 'warning',
              duration: 9000,
              isClosable: true,
            });
          }

          const { error: deleteMakeupSlotErr } = await supabase
            .from('sessions')
            .delete()
            .eq('session_attendance_id', originalAttId)
            .eq('is_makeup', true)
            .eq('valid_from', attendanceDateToCheck);

          if (deleteMakeupSlotErr) {
            console.error('Error removing supposed-makeup calendar row:', deleteMakeupSlotErr);
          }
        }

        // If this day is now "present", drop unfulfilled makeup sessions that were tied to a
        // previous cancellation/absence on this same attendance record (avoids orphaned rows).
        if (attendanceForm.status === 'present' && attendanceId) {
          const { data: linkedMakeups, error: linkedErr } = await supabase
            .from('sessions')
            .select('id')
            .eq('session_attendance_id', attendanceId)
            .eq('is_makeup', true);

          if (linkedErr) {
            console.error('Error loading makeup sessions to clean up:', linkedErr);
          } else if (linkedMakeups?.length) {
            let warnedCompletedMakeup = false;
            for (const m of linkedMakeups) {
              const { data: completedMakeupAttendance } = await supabase
                .from('session_attendance')
                .select('id')
                .eq('session_id', m.id)
                .eq('status', 'present')
                .maybeSingle();

              if (completedMakeupAttendance) {
                if (!warnedCompletedMakeup) {
                  warnedCompletedMakeup = true;
                  toast({
                    title: 'Attendance saved',
                    description:
                      'This session still has a makeup that was already marked present. Remove or adjust it from the schedule if needed.',
                    status: 'warning',
                    duration: 6000,
                    isClosable: true,
                  });
                }
                continue;
              }

              const { error: deleteSessionErr } = await supabase
                .from('sessions')
                .delete()
                .eq('id', m.id);

              if (deleteSessionErr) {
                console.error('Error removing obsolete makeup session:', deleteSessionErr);
              }
            }
          }
        }

        // If a makeup session is cancelled with no-show fee paid, update the original session's madeup_for to NULL
        if (selectedSession.is_makeup && 
            paidNoShowFee && 
            (attendanceForm.status === 'cancelled' || attendanceForm.status === 'absent') &&
            selectedSession.session_attendance_id) {
            
            const { error: originalUpdateError } = await supabase
                .from('session_attendance')
                .update({
                  madeup_for: null,
                  makeup_scheduled: null,
                  voided_linked_makeup_on: null,
                })
                .eq('id', selectedSession.session_attendance_id);

            if (originalUpdateError) {
                console.error('Error updating original session madeup_for:', originalUpdateError);
            } else {
                console.log('Updated original session madeup_for to NULL for attendance ID:', selectedSession.session_attendance_id);
            }
        }

        // Create makeup session if a slot was selected
        if (selectedMakeupSlot &&
            !cancelledMakeupByTherapist &&
            (attendanceForm.status === 'cancelled' || attendanceForm.status === 'absent') &&
            (!attendanceForm.noMakeupRequired || rescheduleMakeup) &&
            !paidNoShowFee &&
            (!selectedSession.is_makeup || rescheduleMakeup)) {
            
            const makeupDate = selectedMakeupSlot.date;
            // Format time without leading zero (e.g., "9:00" not "09:00")
            const rawTime = selectedMakeupSlot.time_slot;
            const hour = parseInt(rawTime.split(':')[0]);
            const makeupTime = `${hour}:00`;
            
            // Get the original cancelled date from the session's attendance record
            // This is important when scheduling from Pending Makeup tab where selectedDate may be different
            const originalCancelledDate = selectedSession.is_makeup && selectedSession.makeup_for
                ? new Date(selectedSession.makeup_for)
                : selectedSession.attendance_date 
                ? new Date(selectedSession.attendance_date) 
                : selectedDate;
            const originalCancelledDateStr = selectedSession.is_makeup && selectedSession.makeup_for
                ? selectedSession.makeup_for
                : selectedSession.attendance_date 
                || getLocalDateString(selectedDate);
            
            // Format the original cancelled date for the note
            const formattedOriginalDate = originalCancelledDate.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric'
            });
            const failedMakeupDateStr =
              selectedSession.attendance_date || getLocalDateString(selectedDate);
            const sessionsTableNote = selectedSession.is_makeup && rescheduleMakeup
                ? `Rescheduled makeup (was ${new Date(failedMakeupDateStr).toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                  })}) for cancelled session (${formattedOriginalDate})`
                : `Makeup for cancelled session (${formattedOriginalDate})`;
            
            // Get client name
            const clientName = selectedSession.client_name || selectedSession.client?.name;
            const makeupTherapistId =
              selectedMakeupSlot.therapist_id ||
              rescheduleTherapistId ||
              selectedSession.therapist.id;
            
            // Use the existing attendance ID if available (when updating from Pending Makeup tab)
            // Otherwise use the newly created attendance ID
            const originalAttendanceId = selectedSession.is_makeup && selectedSession.session_attendance_id
                ? selectedSession.session_attendance_id
                : (selectedSession.attendance?.id || attendanceId);
            
            // Create the makeup session
            const { error: sessionError } = await supabase
                .from('sessions')
                .insert([{
                    therapist_id: makeupTherapistId,
                    client_id: selectedSession.client.id,
                    client_name: clientName,
                    day: DAYS[new Date(makeupDate).getDay()],
                    time: makeupTime,
                    session_type: selectedSession.session_type || 'Regular',
                    notes: sessionsTableNote,
                    valid_from: makeupDate,
                    valid_until: makeupDate,
                    is_makeup: true,
                    makeup_for: originalCancelledDateStr,
                    session_attendance_id: originalAttendanceId
                }]);

            if (sessionError) {
                console.error('Error creating makeup session:', sessionError);
                toast({
                    title: 'Attendance saved, but makeup session failed',
                    description: sessionError.message,
                    status: 'warning',
                    duration: 5000,
                    isClosable: true,
                });
            } else {
                // Mark the slot as assigned (is_open = null) in makeup_session_availability
                const { error: slotUpdateError } = await supabase
                    .from('makeup_session_availability')
                    .update({ 
                        is_open: null,
                        updated_at: new Date().toISOString()
                    })
                    .eq('therapist_id', makeupTherapistId)
                    .eq('date', makeupDate)
                    .eq('time_slot', rawTime); // Use original time format for matching

                if (slotUpdateError) {
                    console.error('Error updating slot availability:', slotUpdateError);
                }

                if (isReschedulingCancelledMakeup && originalAttendanceId) {
                  const { error: keepPendingErr } = await supabase
                    .from('session_attendance')
                    .update({
                      madeup_for: false,
                      makeup_scheduled: true,
                    })
                    .eq('id', originalAttendanceId);

                  if (keepPendingErr) {
                    console.error('Error keeping original makeup pending after reschedule:', keepPendingErr);
                  }
                }

                toast({
                    title: isReschedulingCancelledMakeup ? 'Makeup rescheduled' : 'Makeup session scheduled',
                    description: `Scheduled for ${new Date(makeupDate).toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'short',
                        day: 'numeric'
                    })} at ${hour > 12 ? hour - 12 : hour}:00 ${hour >= 12 ? 'PM' : 'AM'}`,
                    status: 'success',
                    duration: 5000,
                    isClosable: true,
                });
            }
        }
  
        // If status is present, create service fee expense
        if (attendanceData.status === 'present') {
            let serviceFee;
            if (attendanceData.session_type === 'IE') {
                serviceFee = 2200;
            } else {
                serviceFee = await getSessionServiceFeeAsync({
                  session: {
                    ...attendanceData,
                    is_makeup: selectedSession.is_makeup,
                    makeup_for: selectedSession.makeup_for,
                  },
                  therapistSpecialization: selectedSession.therapist.specialization,
                  therapistId: selectedSession.therapist.id,
                  date: getLocalDateString(selectedDate),
                });
            }
    
            // Check if expense record already exists for this session and date to avoid duplicates
            const { data: existingExpense } = await supabase
                .from('expenses')
                .select('id')
                .eq('session_id', selectedSession.id)
                .eq('purchase_date', getLocalDateString(selectedDate))
                .eq('expense_type', 'Service Fee')
                .single();

            if (!existingExpense) {
                // Only insert if no existing expense record found
                const expenseData = {
                    purchase_date: getLocalDateString(selectedDate),
                    expense_name: `${selectedSession.therapist.name} (${selectedSession.client.name})`,
                    expense_type: 'Service Fee',
                    amount: serviceFee,
                    receipt_type: 'None',
                    notes: '',
                    session_id: selectedSession.id, // Use session_id instead of session_attendance_id
                    session_attendance_id: attendanceId // Keep this for backward compatibility if needed
                };
        
                const { error: expenseError } = await supabase
                    .from('expenses')
                    .insert([expenseData]);
        
                if (expenseError) throw expenseError;
            }
        }

        if ((attendanceForm.isPromo || matchingService?.promo_count > 0) && 
            attendanceForm.status === 'present') {

            // If we've reached 10 sessions, reset back to null
            if (newPromoCount === 10) {
                newPromoCount = null;
            }

            const { error: promoError } = await supabase
                .from('client_services')
                .update({ promo_count: newPromoCount })
                .eq('client_id', selectedSession.client.id)
                .eq('service_type', billingServiceType);

            if (promoError) throw promoError;
        }
  
        // Update client document status and OR information
        const documentsToUpdate = {
            ...(documentStatus.initial_document && { initial_document: true }),
            ...(documentStatus.given_ca && { given_ca: true }),
            ...(documentStatus.signed_ca && { signed_ca: true }),
            ...(documentStatus.signed_cf && { signed_cf: true }),
            ...(documentStatus.pwd_card && { pwd_card: true }),
        };

        if (attendanceForm.status === 'present' && attendanceForm.isPaid) {
            documentsToUpdate.official_receipt = documentStatus.official_receipt;
            documentsToUpdate.or_name = documentStatus.official_receipt
                ? attendanceForm.orName
                : null;
        }
  
        if (Object.keys(documentsToUpdate).length > 0) {
            const { error: clientUpdateError } = await supabase
                .from('clients')
                .update(documentsToUpdate)
                .eq('id', selectedSession.client.id);
    
            if (clientUpdateError) throw clientUpdateError;
        }
  
        // Update session type to Regular if conditions are met
        if (attendanceForm.status === 'present' && 
            (selectedSession.session_type === 'IE' || 
            selectedSession.session_type === 'Endorsement')) {
            const { error: sessionUpdateError } = await supabase
                .from('sessions')
                .update({ session_type: 'Regular' })
                .eq('id', selectedSession.id)
                .eq('day', selectedSession.day)  // Add these two lines
                .eq('time', selectedSession.time);

            if (sessionUpdateError) {
                console.error('Error updating session type:', sessionUpdateError);
                throw sessionUpdateError;
            }

            // Also update the selectedSession to reflect the change
            selectedSession.session_type = 'Regular';
        }

        // Update makeup tracking if this is a makeup session marked as present
        if (attendanceForm.status === 'present' && 
            selectedSession.is_makeup === true && 
            selectedSession.session_attendance_id) {
            
            console.log('Updating makeup tracking for session_attendance_id:', selectedSession.session_attendance_id);
            
            const { error: makeupUpdateError } = await supabase
                .from('session_attendance')
                .update({ madeup_for: true })
                .eq('id', selectedSession.session_attendance_id);

            if (makeupUpdateError) {
                console.error('Error updating makeup tracking:', makeupUpdateError);
                // Don't throw error - just log it so the main attendance recording still succeeds
            } else {
                console.log('Successfully updated madeup_for to TRUE for original cancelled session');
            }
        }
  
        toast({
            title: existingAttendance ? 'Attendance Updated' : 'Attendance Recorded',
            status: 'success',
            duration: 3000,
            isClosable: true,
        });
  
        setIsAttendanceModalOpen(false);
        
        // IMPORTANT: Call refresh functions to update all views
        await fetchSessions();
        await fetchMakeupOpportunities();
        if (view === 'unpaid') {
            await fetchUnpaidSessions();
        }
        if (view === 'pending-makeup') {
            await fetchPendingMakeupSessions();
        }
        
    } catch (error) {
      toast({
        title: 'Error recording attendance',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingAttendance(false);
    }
  };

  const handleNotesUpdate = async () => {
    try {
      const { error } = await supabase
        .from('clients')
        .update({ notes: sessionNotes })
        .eq('id', selectedSession.client_id);

      if (error) throw error;

      toast({
        title: 'Notes Updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setIsNotesModalOpen(false);
      fetchSessions();
    } catch (error) {
      toast({
        title: 'Error updating notes',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Helper functions for date and time formatting
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleDateChange = (event) => {
    setSelectedDate(new Date(event.target.value));
  };

  const formatTime = useCallback((time) => {
    const hour = parseInt(time.split(':')[0]);
    const h = hour % 12 || 12;
    const ampm = hour < 12 ? 'AM' : 'PM';
    return `${h}:00 ${ampm}`;
  }, []);

  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount);
  }, []);

  const navigateDay = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(selectedDate.getDate() + direction);
    setSelectedDate(newDate);
  };

  const goToTomorrow = useCallback(() => {
    setSelectedDate(getTomorrowLocalDate());
  }, []);

  const handleDailyScheduleViewChange = useCallback((index) => {
    const nextView = dailyScheduleViews[index] || 'clients';
    if (nextView === 'new-client-reminders') {
      // Set tomorrow in the same event as the tab switch so the fetch effect
      // never runs with stale "today" selectedDate (race after midnight / on refresh).
      setSelectedDate(getTomorrowLocalDate());
    }
    setView(nextView);
  }, [dailyScheduleViews]);

  const handleScheduleMakeupOpportunity = useCallback((opportunity) => {
    setOneTimeSessionForm({
      therapist_id: opportunity.therapistId,
      client_id: opportunity.clientId,
      time: opportunity.time,
      notes: '',
      pending_makeup_id: opportunity.pendingMakeupId,
    });
    setIsOneTimeModalOpen(true);
  }, []);

  const isReminderTargetTomorrow = useMemo(() => {
    return getLocalDateString(selectedDate) === getLocalDateString(getTomorrowLocalDate());
  }, [selectedDate]);

  const makeupOpportunityCount = useMemo(
    () => makeupOpportunities.reduce((sum, item) => sum + item.matches.length, 0),
    [makeupOpportunities]
  );

  const getSessionsForTimeSlot = useCallback((timeSlot) => {
    return sessions.filter(session => session.time === timeSlot);
  }, [sessions]);

  const unpaidSessionsByDate = useMemo(() => {
    const groupedSessions = {};
    unpaidSessions.forEach(session => {
      const date = session.attendance_date;
      if (!groupedSessions[date]) {
        groupedSessions[date] = [];
      }
      groupedSessions[date].push(session);
    });
    
    Object.keys(groupedSessions).forEach(date => {
      groupedSessions[date].sort((a, b) => parseInt(a.time) - parseInt(b.time));
    });
    
    return groupedSessions;
  }, [unpaidSessions]);

  const filteredPendingMakeupSessions = useMemo(() => {
    if (view !== 'pending-makeup' || pendingMakeupViewMode === 'by-client') {
      return pendingMakeupSessions;
    }

    const rangeStart = new Date();
    rangeStart.setDate(rangeStart.getDate() - pendingMakeupDateRange);
    const rangeStartStr = getLocalDateString(rangeStart);

    return pendingMakeupSessions.filter(
      (session) => session.attendance_date >= rangeStartStr
    );
  }, [pendingMakeupSessions, pendingMakeupViewMode, pendingMakeupDateRange, view]);

  const pendingMakeupSessionsByDate = useMemo(() => {
    const groupedSessions = {};
    filteredPendingMakeupSessions.forEach(session => {
      const date = session.attendance_date;
      if (!groupedSessions[date]) {
        groupedSessions[date] = [];
      }
      groupedSessions[date].push(session);
    });
    
    Object.keys(groupedSessions).forEach(date => {
      groupedSessions[date].sort((a, b) => parseInt(a.time) - parseInt(b.time));
    });
    
    return groupedSessions;
  }, [filteredPendingMakeupSessions]);

  const memoizedSessionCards = useMemo(() => {
    return sessions.map(session => {
      const sessionKey = `${session.id}-${session.attendance?.id || 'no-attendance'}`;
      
      return (
        <SessionCard 
          key={sessionKey}
          session={session}
          selectedDate={selectedDate}
          setSelectedSession={handleSetSelectedSession}
          setAttendanceForm={handleSetAttendanceForm}
          setDocumentStatus={setDocumentStatus}
          setIsAttendanceModalOpen={handleSetIsAttendanceModalOpen}
          setPaidNoShowFee={setPaidNoShowFee}
          setCancelledMakeupByTherapist={setCancelledMakeupByTherapist}
          setRescheduleMakeup={setRescheduleMakeup}
          setGraceCancellation={setGraceCancellation}
          setWaivedLateCancellation={setWaivedLateCancellation}
          setSessionNotes={handleSetSessionNotes}
          setIsNotesModalOpen={handleSetIsNotesModalOpen}
          calculatePrice={calculatePrice}
          formatCurrency={formatCurrency}
          formatTime={formatTime}
          onRefresh={handleRefresh}
        />
      );
    });
  }, [
    sessions, 
    selectedDate, 
    handleSetSelectedSession, 
    handleSetAttendanceForm, 
    handleSetIsAttendanceModalOpen,
    setPaidNoShowFee,
    setCancelledMakeupByTherapist,
    setRescheduleMakeup,
    handleSetSessionNotes, 
    handleSetIsNotesModalOpen, 
    calculatePrice, 
    formatCurrency, 
    formatTime,
    handleRefresh
  ]);

  return (
    <Box>
      {view !== SESSION_TRENDS_VIEW && (
      <HStack justify="space-between" mb={6}>
        <IconButton
          icon={<ChevronLeftIcon />}
          onClick={() => navigateDay(-1)}
          aria-label="Previous day"
        />
        <Popover>
          <PopoverTrigger>
            <Heading size="md" cursor="pointer" display="flex" alignItems="center">
              {formatDate(selectedDate)}
              <CalendarIcon ml={2} />
            </Heading>
          </PopoverTrigger>
          <PopoverContent>
            <PopoverArrow />
            <PopoverCloseButton />
            <PopoverBody>
              <FormControl>
                <FormLabel>Select a date</FormLabel>
                <Input
                  type="date"
                  value={getLocalDateString(selectedDate)}
                  onChange={handleDateChange}
                />
              </FormControl>
            </PopoverBody>
          </PopoverContent>
        </Popover>
        <IconButton
          icon={<ChevronRightIcon />}
          onClick={() => navigateDay(1)}
          aria-label="Next day"
        />
      </HStack>
      )}

      <Tabs onChange={handleDailyScheduleViewChange} mb={4}>
        <TabList>
          <Tab>Client Schedule</Tab>
          <Tab>Schedule Reminders</Tab>
          <Tab>Therapist Summary</Tab>
          <Tab>Unpaid Sessions</Tab>
          <Tab>Pending Makeup / No-Show</Tab>
          <Tab>
            <HStack spacing={2}>
              <Text>Makeup Opportunities</Text>
              <Badge
                colorScheme={makeupOpportunityCount > 0 ? 'orange' : 'gray'}
                variant="solid"
                borderRadius="full"
                minW="22px"
                textAlign="center"
                fontSize="xs"
              >
                {makeupOpportunityCount}
              </Badge>
            </HStack>
          </Tab>
          {showSessionTrends && <Tab>Session Trends</Tab>}
        </TabList>
      </Tabs>

      {view === 'clients' &&       
        <HStack justify="space-between">
            <Spacer/>
            <Button
                leftIcon={<BiCalculator />}  // You'll need to import this icon
                colorScheme="teal"
                size="sm"
                mb={4}
                mr={2}
                onClick={() => setIsVATCalculatorOpen(true)}
            >
            VAT Calculator
            </Button>
            <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            size="sm"
            mb={4}
            mr={4}
            onClick={() => setIsOneTimeModalOpen(true)}
            >
            Add One-Time Session
            </Button>
        </HStack>
      }
  
      {view === 'clients' ? (
      <VStack align="stretch" spacing={4}>
        {TIME_SLOTS.map(timeSlot => {
          const timeSlotSessions = sessions.filter(session => session.time === timeSlot);
          if (timeSlotSessions.length === 0) return null;

          return (
            <Box key={timeSlot} p={4} borderWidth={1} borderRadius="md">
              <Heading size="sm" mb={4}>{formatTime(timeSlot)}</Heading>
              <Box 
                display="grid" 
                gridTemplateColumns={{
                  base: "1fr",
                  md: "repeat(2, 1fr)",
                  lg: "repeat(3, 1fr)"
                }}
                gap={3}
              >
                {memoizedSessionCards.filter(cardElement => 
                  timeSlotSessions.some(session => 
                    cardElement.key.startsWith(`${session.id}-`)
                  )
                )}
              </Box>
            </Box>
          );
        }).filter(Boolean)}
      </VStack>
      ) : view === 'new-client-reminders' ? (
        <VStack align="stretch" spacing={3}>
          {!isReminderTargetTomorrow && (
            <HStack justify="flex-end">
              <Button size="sm" colorScheme="orange" variant="outline" onClick={goToTomorrow}>
                Jump to Tomorrow
              </Button>
            </HStack>
          )}
          <NewClientRemindersView
            sessions={newClientReminderSessions}
            loading={newClientRemindersLoading}
            targetDate={selectedDate}
            isTomorrow={isReminderTargetTomorrow}
            formatTime={formatTime}
          />
        </VStack>
      ) : view === 'therapists' ? (
        <VStack align="stretch" spacing={4}>
          <Select
            value={selectedTherapist}
            onChange={(e) => setSelectedTherapist(e.target.value)}
            maxW="400px"
          >
            {therapists.map(therapist => (
              <option key={therapist.id} value={therapist.id}>
                {therapist.name} - {therapist.specialization}
              </option>
            ))}
          </Select>
          <TherapistSchedule 
            key={`${selectedTherapist}-${getLocalDateString(selectedDate)}-${view}`}
            therapistId={selectedTherapist} 
            selectedDate={selectedDate}
          />
        </VStack>
      ) : view === 'unpaid' ? (
        // Unpaid Sessions View
        <VStack align="stretch" spacing={4}>
          <HStack justify="space-between" align="center" flexWrap="wrap" gap={2}>
            <Heading size="md">Unpaid Sessions</Heading>
            <HStack>
              <Text fontSize="sm">Show last:</Text>
              <Select
                value={unpaidDateRange}
                onChange={(e) => setUnpaidDateRange(parseInt(e.target.value))}
                size="sm"
                maxW="120px"
              >
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
              </Select>
            </HStack>
          </HStack>

          <Box p={4} borderWidth={1} borderRadius="md" bg="orange.50" borderColor="orange.200">
            <HStack justify="space-between" flexWrap="wrap">
              <Text fontWeight="semibold" color="orange.800">
                Total Receivables (Last 3 Months)
              </Text>
              <HStack spacing={4}>
                <Badge colorScheme="orange">{totalReceivables90.count} unpaid sessions</Badge>
                <Text fontWeight="bold" color="orange.900">
                  {formatCurrency(totalReceivables90.amount)}
                </Text>
              </HStack>
            </HStack>
          </Box>
          
          {unpaidLoading ? (
            <Center py={8}>
              <Spinner size="lg" />
            </Center>
          ) : (
            <VStack align="stretch" spacing={4}>
              {Object.entries(unpaidSessionsByDate)
                .sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA)) // Sort dates descending
                .map(([date, dateSessions]) => (
                <Box key={date} p={4} borderWidth={1} borderRadius="md">
                  <Heading size="sm" mb={4}>
                    {new Date(date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                    <Badge ml={2} colorScheme="red">{dateSessions.length} unpaid</Badge>
                  </Heading>
                  <Box 
                    display="grid" 
                    gridTemplateColumns={{
                      base: "1fr",
                      md: "repeat(2, 1fr)",
                      lg: "repeat(3, 1fr)"
                    }}
                    gap={3}
                  >
                    {dateSessions.map(session => (
                      <SessionCard 
                        key={`${session.id}-${session.attendance_date}`}
                        session={session}
                        selectedDate={new Date(session.attendance_date)}
                        setSelectedSession={setSelectedSession}
                        setAttendanceForm={setAttendanceForm}
                        setDocumentStatus={setDocumentStatus}
                        setIsAttendanceModalOpen={setIsAttendanceModalOpen}
                        setPaidNoShowFee={setPaidNoShowFee}
                        setCancelledMakeupByTherapist={setCancelledMakeupByTherapist}
                        setRescheduleMakeup={setRescheduleMakeup}
                        setGraceCancellation={setGraceCancellation}
                        setWaivedLateCancellation={setWaivedLateCancellation}
                        setSessionNotes={setSessionNotes}
                        setIsNotesModalOpen={setIsNotesModalOpen}
                        calculatePrice={calculatePrice}
                        formatCurrency={formatCurrency}
                        formatTime={formatTime}
                        onRefresh={async () => {
                          await fetchSessions();
                          await fetchUnpaidSessions(); // Refresh both views
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
              
              {unpaidSessions.length === 0 && (
                <Box textAlign="center" py={8}>
                  <Text color="gray.500">No unpaid sessions found in the last {unpaidDateRange} days</Text>
                </Box>
              )}
            </VStack>
          )}
        </VStack>
      ) : view === 'pending-makeup' ? (
        // Pending Makeup Sessions View (includes pending no-show fees)
        <VStack align="stretch" spacing={4}>
          <HStack justify="space-between" align="center" flexWrap="wrap" gap={2}>
            <Heading size="md">Pending Makeup / No-Show Fees</Heading>
            <HStack flexWrap="wrap" gap={2}>
              <ButtonGroup size="sm" isAttached variant="outline">
                <Button
                  colorScheme={pendingMakeupViewMode === 'by-date' ? 'blue' : 'gray'}
                  variant={pendingMakeupViewMode === 'by-date' ? 'solid' : 'outline'}
                  onClick={() => setPendingMakeupViewMode('by-date')}
                >
                  By Date
                </Button>
                <Button
                  colorScheme={pendingMakeupViewMode === 'by-client' ? 'blue' : 'gray'}
                  variant={pendingMakeupViewMode === 'by-client' ? 'solid' : 'outline'}
                  onClick={() => setPendingMakeupViewMode('by-client')}
                >
                  By Client
                </Button>
              </ButtonGroup>
              {pendingMakeupViewMode === 'by-date' && (
                <>
                  <Text fontSize="sm">Show last:</Text>
                  <Select
                    value={pendingMakeupDateRange}
                    onChange={(e) => setPendingMakeupDateRange(parseInt(e.target.value))}
                    size="sm"
                    maxW="120px"
                  >
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                    <option value={60}>60 days</option>
                    <option value={90}>90 days</option>
                  </Select>
                </>
              )}
            </HStack>
          </HStack>

          {pendingMakeupViewMode === 'by-date' && (
            <Text fontSize="sm" color="gray.600">
              Summary shows all active clients; list below filtered by date range
            </Text>
          )}

          <Box p={4} borderWidth={1} borderRadius="md" bg="purple.50" borderColor="purple.200">
            <HStack justify="space-between" flexWrap="wrap">
              <Text fontWeight="semibold" color="purple.800">
                Total Pending Makeup Sessions (All Active Clients)
              </Text>
              <Badge colorScheme="purple" fontSize="md" px={3} py={1}>
                {pendingMakeupSessions.length} pending
              </Badge>
            </HStack>
          </Box>
          
          {pendingMakeupLoading ? (
            <Center py={8}>
              <Spinner size="lg" />
            </Center>
          ) : pendingMakeupViewMode === 'by-client' ? (
            <PendingMakeupByClientView
              pendingSessions={pendingMakeupSessions}
              scheduledSessions={scheduledFutureMakeupSessions}
            />
          ) : (
            <VStack align="stretch" spacing={4}>
              {Object.entries(pendingMakeupSessionsByDate)
                .sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA)) // Sort dates descending
                .map(([date, dateSessions]) => (
                <Box key={date} p={4} borderWidth={1} borderRadius="md">
                  <Heading size="sm" mb={4}>
                    {new Date(date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                    <Badge ml={2} colorScheme="purple">{dateSessions.length} pending</Badge>
                  </Heading>
                  <Box 
                    display="grid" 
                    gridTemplateColumns={{
                      base: "1fr",
                      md: "repeat(2, 1fr)",
                      lg: "repeat(3, 1fr)"
                    }}
                    gap={3}
                  >
                    {dateSessions.map(session => (
                      <SessionCard 
                        key={`${session.id}-${session.attendance_date}`}
                        session={session}
                        selectedDate={new Date(session.attendance_date)}
                        setSelectedSession={setSelectedSession}
                        setAttendanceForm={setAttendanceForm}
                        setDocumentStatus={setDocumentStatus}
                        setIsAttendanceModalOpen={setIsAttendanceModalOpen}
                        setPaidNoShowFee={setPaidNoShowFee}
                        setCancelledMakeupByTherapist={setCancelledMakeupByTherapist}
                        setRescheduleMakeup={setRescheduleMakeup}
                        setGraceCancellation={setGraceCancellation}
                        setWaivedLateCancellation={setWaivedLateCancellation}
                        setSessionNotes={setSessionNotes}
                        setIsNotesModalOpen={setIsNotesModalOpen}
                        calculatePrice={calculatePrice}
                        formatCurrency={formatCurrency}
                        formatTime={formatTime}
                        onRefresh={async () => {
                          await fetchSessions();
                          await fetchPendingMakeupSessions(); // Refresh pending makeup view
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
              
              {filteredPendingMakeupSessions.length === 0 && (
                <Box textAlign="center" py={8}>
                  <Text color="gray.500">No pending makeup sessions or no-show fees found in the last {pendingMakeupDateRange} days</Text>
                </Box>
              )}
            </VStack>
          )}
        </VStack>
      ) : view === 'makeup-opportunities' ? (
        <MakeupOpportunitiesView
          opportunities={makeupOpportunities}
          loading={makeupOpportunitiesLoading}
          targetDate={selectedDate}
          formatTime={formatTime}
          onScheduleOpportunity={handleScheduleMakeupOpportunity}
        />
      ) : showSessionTrends ? (
        <SessionTrendsChart isVisible={isVisible && view === SESSION_TRENDS_VIEW} />
      ) : null}
  
      {!isLoading && sessions.length === 0 && view === 'clients' && (
        <Box textAlign="center" py={8}>
          <Text color="gray.500">No sessions scheduled for this day</Text>
        </Box>
      )}
  
      {/* Attendance Modal */}
      <Modal 
        isOpen={isAttendanceModalOpen} 
        onClose={() => setIsAttendanceModalOpen(false)}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Record Attendance
            {selectedSession && ` - ${selectedSession.client.name}`}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                  value={attendanceForm.status}
                  onChange={(e) => setAttendanceForm({
                    ...attendanceForm,
                    status: e.target.value,
                    noMakeupRequired: (e.target.value === 'cancelled' || e.target.value === 'absent') ? attendanceForm.noMakeupRequired : false  // Reset when not cancelled or absent
                  })}
                >
                  <option value="present">Present</option>
                  <option value="absent">Late Cancellation</option>
                  <option value="cancelled">Cancelled</option>
                </Select>
              </FormControl>
  
              {attendanceForm.status === 'present' && selectedSession && (
                <>
                {selectedSession.session_type !== 'IE' && (
                <FormControl>
                    <Checkbox
                    isChecked={attendanceForm.isPromo}
                    onChange={async (e) => {
                        const isPromo = e.target.checked;
                        const selectedDateStr = getLocalDateString(selectedDate);
                        
                        const matchingService = selectedSession.client?.client_services?.find(service => {
                          const serviceMatches = service.service_type === selectedSession.service_type;
                          const dateMatches = (!service.pricing_valid_from || service.pricing_valid_from <= selectedDateStr) &&
                                             (!service.pricing_valid_until || service.pricing_valid_until >= selectedDateStr);
                          return serviceMatches && dateMatches;
                        });
                      
                        let promoAmount;
                        if (isPromo) {
                          promoAmount = await getPackageRate(
                            selectedSession.service_type,
                            selectedSession.client.pwd,
                            matchingService?.vat_inclusive
                          );
                        }
                      
                        setAttendanceForm(prev => ({
                          ...prev,
                          isPromo,
                          amountPaid: isPromo ? 
                            (promoAmount ? promoAmount.toString() : 
                              selectedSession.client.pwd ? 
                                (selectedSession.base_rate * 0.8 * 10) : 
                                (selectedSession.base_rate * 10)
                            ).toString() : 
                            selectedSession.discounted_rate.toString()
                        }));
                    }}
                    isDisabled={selectedSession.client?.client_services?.find(service => 
                        service.service_type === selectedSession.service_type
                    )?.promo_count > 0}
                    >
                    Promo Package (10 Sessions)
                    </Checkbox>
                </FormControl>
                )}
  
                  <FormControl>
                    <FormLabel>Amount Paid</FormLabel>
                    {(selectedSession.session_type === 'IE' || selectedSession.session_type === 'Endorsement') && (
                      <ReservationFeeDisplay 
                        selectedSession={selectedSession}
                        selectedDate={selectedDate}
                        formatCurrency={formatCurrency}
                      />
                    )}
                    
                    {/* Add promo session display */}
                    {(selectedSession.attendance?.promo_session || 
                    (selectedSession.client?.client_services?.find(service => {
                        const specializationToService = {
                          'Speech Therapy': 'ST',
                          'Occupational Therapy': 'OT', 
                          'Physical Therapy': 'PT',
                          'Play Group': 'PG'
                        };
                        const serviceMatches = service.service_type === specializationToService[selectedSession.therapist.specialization];
                        const dateMatches = (!service.pricing_valid_from || service.pricing_valid_from <= getLocalDateString(selectedDate)) &&
                                          (!service.pricing_valid_until || service.pricing_valid_until >= getLocalDateString(selectedDate));
                        return serviceMatches && dateMatches;
                    })?.promo_count > 0 && 
                    selectedSession.client?.client_services?.find(service => {
                        const specializationToService = {
                          'Speech Therapy': 'ST',
                          'Occupational Therapy': 'OT',
                          'Physical Therapy': 'PT', 
                          'Play Group': 'PG'
                        };
                        const serviceMatches = service.service_type === specializationToService[selectedSession.therapist.specialization];
                        const dateMatches = (!service.pricing_valid_from || service.pricing_valid_from <= getLocalDateString(selectedDate)) &&
                                          (!service.pricing_valid_until || service.pricing_valid_until >= getLocalDateString(selectedDate));
                        return serviceMatches && dateMatches;
                    })?.promo_count < 10)) && (
                    <Text fontSize="sm" color="blue.600" mb={2}>
                        {selectedSession.attendance ? 
                        `Promo Session ${selectedSession.attendance.promo_session}/10` :
                        `Promo Session ${(() => {
                            const matchingService = selectedSession.client.client_services.find(service => {
                                const specializationToService = {
                                  'Speech Therapy': 'ST',
                                  'Occupational Therapy': 'OT',
                                  'Physical Therapy': 'PT',
                                  'Play Group': 'PG'
                                };
                                const serviceMatches = service.service_type === specializationToService[selectedSession.therapist.specialization];
                                const dateMatches = (!service.pricing_valid_from || service.pricing_valid_from <= getLocalDateString(selectedDate)) &&
                                                  (!service.pricing_valid_until || service.pricing_valid_until >= getLocalDateString(selectedDate));
                                return serviceMatches && dateMatches;
                            });
                            return matchingService ? `${matchingService.promo_count + 1}/10` : '1/10';
                        })()}`
                        }
                    </Text>
                    )}
                    
                    <NumberInput
                      value={attendanceForm.amountPaid}
                      onChange={(valueString) => setAttendanceForm({
                        ...attendanceForm,
                        amountPaid: valueString
                      })}
                      min={0}
                      isDisabled={selectedSession.client?.client_services?.find(service => 
                        service.service_type === selectedSession.service_type
                      )?.promo_count > 0 && selectedSession.client?.client_services?.find(service => 
                        service.service_type === selectedSession.service_type
                      )?.promo_count < 10}
                    >
                      <NumberInputField />
                    </NumberInput>
                  </FormControl>

                  {/* Add this new FormControl */}
                  <FormControl>
                    <Checkbox
                        isChecked={attendanceForm.isPaid}
                        onChange={(e) => setAttendanceForm(prev => ({
                            ...prev,
                            isPaid: e.target.checked,
                            orNumber: e.target.checked ? prev.orNumber : '',
                            centerOrNumber: e.target.checked ? prev.centerOrNumber : '',
                            therapistOrNumber: e.target.checked ? prev.therapistOrNumber : '',
                            orName: e.target.checked ? prev.orName : ''
                        }))}
                    >
                        Payment Received
                    </Checkbox>
                  </FormControl>

                  {attendanceForm.isPaid && (() => {
                    const matchingService = selectedSession.client?.client_services?.find(service => {
                      const specializationToService = {
                        'Speech Therapy': 'ST',
                        'Occupational Therapy': 'OT',
                        'Physical Therapy': 'PT',
                        'Play Group': 'PG'
                      };
                      const serviceMatches = service.service_type === specializationToService[selectedSession.therapist.specialization];
                      const dateMatches = (!service.pricing_valid_from || service.pricing_valid_from <= getLocalDateString(selectedDate)) &&
                        (!service.pricing_valid_until || service.pricing_valid_until >= getLocalDateString(selectedDate));
                      return serviceMatches && dateMatches;
                    });
                    const useSplitReceiptsInModal = shouldUseSplitReceipts(
                      selectedSession.therapist,
                      getLocalDateString(selectedDate)
                    );

                    return (
                      <>
                        <FormControl>
                          <FormLabel>Request Official Receipt</FormLabel>
                          <Checkbox
                            isChecked={documentStatus.official_receipt}
                            onChange={(e) => setDocumentStatus(prev => ({
                              ...prev,
                              official_receipt: e.target.checked
                            }))}
                          >
                            Requests OR
                          </Checkbox>
                        </FormControl>

                        {documentStatus.official_receipt && (
                          <>
                            <FormControl>
                              <FormLabel>OR Name</FormLabel>
                              <Input
                                value={attendanceForm.orName}
                                onChange={(e) => setAttendanceForm({
                                  ...attendanceForm,
                                  orName: e.target.value
                                })}
                                placeholder="Enter name to appear on OR"
                              />
                            </FormControl>

                            {useSplitReceiptsInModal ? (
                              <FormControl>
                                <FormLabel>
                                  {sameDayReceiptSummary?.sessionCount >= 2 && sameDayReceiptSummary.mode === 'split'
                                    ? 'Center OR Number (all center fees today)'
                                    : 'Center OR Number'}
                                </FormLabel>
                                <Input
                                  value={attendanceForm.centerOrNumber}
                                  onChange={(e) => setAttendanceForm({
                                    ...attendanceForm,
                                    centerOrNumber: e.target.value
                                  })}
                                  placeholder="Center receipt OR number"
                                />
                              </FormControl>
                            ) : (
                              <FormControl>
                                <FormLabel>
                                  {sameDayReceiptSummary?.sessionCount >= 2 && sameDayReceiptSummary.mode === 'center'
                                    ? 'OR Number (combined for all sessions today)'
                                    : 'OR Number'}
                                </FormLabel>
                                <Input
                                  value={attendanceForm.orNumber}
                                  onChange={(e) => setAttendanceForm({
                                    ...attendanceForm,
                                    orNumber: e.target.value
                                  })}
                                  placeholder="Enter OR number"
                                />
                              </FormControl>
                            )}

                            {useSplitReceiptsInModal && (
                              <FormControl>
                                <FormLabel>
                                  {sameDayReceiptSummary?.sameTherapist
                                    ? 'Therapist OR Number (both sessions)'
                                    : 'Therapist OR Number'}
                                </FormLabel>
                                <Input
                                  value={attendanceForm.therapistOrNumber}
                                  onChange={(e) => setAttendanceForm({
                                    ...attendanceForm,
                                    therapistOrNumber: e.target.value
                                  })}
                                  placeholder="Therapist receipt OR number"
                                />
                              </FormControl>
                            )}

                            {selectedSession.client.pwd_number && (
                              <HStack justify="center" width="100%">
                                <Text>PWD Number: </Text>
                                <Text>{selectedSession.client.pwd_number}</Text>
                              </HStack>
                            )}

                            {sameDayReceiptSummary?.sessionCount >= 2 ? (
                              <Box mt={0} p={4} borderWidth={1} borderRadius="md" width="100%">
                                <CombinedSameDayReceiptGuide
                                  summary={sameDayReceiptSummary}
                                  formatCurrency={formatCurrency}
                                  formatTime={formatTime}
                                />
                              </Box>
                            ) : useSplitReceiptsInModal && receiptSplitPreview && !receiptSplitPreview.therapistReceiptOnly ? (
                              <Box mt={0} p={4} borderWidth={1} borderRadius="md" width="100%">
                                <SplitReceiptGuide
                                  totalDue={receiptSplitPreview.displayTotalDue ?? (parseFloat(attendanceForm.amountPaid) || 0)}
                                  therapistFee={receiptSplitPreview.therapistFee}
                                  isVatInclusive={matchingService?.vat_inclusive}
                                  formatCurrency={formatCurrency}
                                  isNewPromoPackage={receiptSplitPreview.usePerSessionCenterReceipt}
                                  isOngoingPromoPackageSession={receiptSplitPreview.isOngoingPromoPackageSession}
                                />
                              </Box>
                            ) : !useSplitReceiptsInModal && matchingService?.vat_inclusive && (
                              <Box mt={0} p={4} borderWidth={1} borderRadius="md">
                                <Text fontWeight="bold" mb={2}>VAT Breakdown</Text>
                                <VATBreakdown
                                  selectedSession={selectedSession}
                                  matchingService={matchingService}
                                  attendanceForm={attendanceForm}
                                />
                              </Box>
                            )}

                            {sameDayReceiptSummary?.mode === 'mixed' && useSplitReceiptsInModal && receiptSplitPreview && (
                              <Box mt={0} p={4} borderWidth={1} borderRadius="md" width="100%">
                                <Text fontWeight="bold" mb={2}>This Session Only</Text>
                                <SplitReceiptGuide
                                  totalDue={receiptSplitPreview.displayTotalDue ?? (parseFloat(attendanceForm.amountPaid) || 0)}
                                  therapistFee={receiptSplitPreview.therapistFee}
                                  isVatInclusive={matchingService?.vat_inclusive}
                                  formatCurrency={formatCurrency}
                                  therapistReceiptOnly={receiptSplitPreview.therapistReceiptOnly}
                                  isNewPromoPackage={receiptSplitPreview.usePerSessionCenterReceipt}
                                  isOngoingPromoPackageSession={receiptSplitPreview.isOngoingPromoPackageSession}
                                />
                              </Box>
                            )}

                            {sameDayReceiptSummary?.mode === 'mixed' && !useSplitReceiptsInModal && matchingService?.vat_inclusive && (
                              <Box mt={0} p={4} borderWidth={1} borderRadius="md" width="100%">
                                <Text fontWeight="bold" mb={2}>This Session Only</Text>
                                <VATBreakdown
                                  selectedSession={selectedSession}
                                  matchingService={matchingService}
                                  attendanceForm={attendanceForm}
                                />
                              </Box>
                            )}
                          </>
                        )}
                      </>
                    );
                  })()}

                  {(() => {
                    const selectedDateStr = getLocalDateString(selectedDate);
                    const billingServiceType = resolveBillingServiceType(
                      selectedSession,
                      selectedSession.therapist,
                      selectedSession.client?.client_services,
                      selectedDateStr
                    );
                    const matchingService = findMatchingClientService(
                      selectedSession.client?.client_services,
                      selectedSession.therapist.specialization,
                      selectedDateStr,
                      billingServiceType
                    );
                    const useSplitReceiptsInModal = shouldUseSplitReceipts(
                      selectedSession.therapist,
                      selectedDateStr
                    );

                    if (
                      !attendanceForm.isPaid ||
                      !useSplitReceiptsInModal ||
                      !documentStatus.official_receipt ||
                      !receiptSplitPreview?.therapistReceiptOnly ||
                      !receiptSplitPreview ||
                      sameDayReceiptSummary?.sessionCount >= 2
                    ) {
                      return null;
                    }

                    return (
                      <Box mt={0} p={4} borderWidth={1} borderRadius="md" width="100%">
                        <SplitReceiptGuide
                          totalDue={receiptSplitPreview.displayTotalDue ?? (parseFloat(attendanceForm.amountPaid) || 0)}
                          therapistFee={receiptSplitPreview.therapistFee}
                          isVatInclusive={matchingService?.vat_inclusive}
                          formatCurrency={formatCurrency}
                          therapistReceiptOnly
                        />
                      </Box>
                    );
                  })()}
                </>
              )}
  
              {selectedSession && (
                <FormControl>
                  {(!selectedSession.client.initial_document || !selectedSession.client.given_ca || !selectedSession.client.signed_ca || !selectedSession.client.signed_cf || (selectedSession.client.pwd && !selectedSession.client.pwd_card) ) &&
                  <FormLabel>Update Document Status</FormLabel>
                  }
                  <VStack align="start" spacing={2}>
                    {!selectedSession.client.initial_document && (
                      <Checkbox
                        isChecked={documentStatus.initial_document}
                        onChange={(e) => setDocumentStatus(prev => ({
                          ...prev,
                          initial_document: e.target.checked
                        }))}
                      >
                        Initial Document Submitted
                      </Checkbox>
                    )}
                    {!selectedSession.client.given_ca && (
                      <Checkbox
                        isChecked={documentStatus.given_ca}
                        onChange={(e) => setDocumentStatus(prev => ({
                          ...prev,
                          given_ca: e.target.checked
                        }))}
                      >
                        Client Agreement Given
                      </Checkbox>
                    )}
                    {!selectedSession.client.signed_ca && (
                      <Checkbox
                        isChecked={documentStatus.signed_ca}
                        onChange={(e) => setDocumentStatus(prev => ({
                          ...prev,
                          signed_ca: e.target.checked
                        }))}
                      >
                        Client Agreement Signed
                      </Checkbox>
                    )}
                    {!selectedSession.client.signed_cf && (
                      <Checkbox
                        isChecked={documentStatus.signed_cf}
                        onChange={(e) => setDocumentStatus(prev => ({
                          ...prev,
                          signed_cf: e.target.checked
                        }))}
                      >
                        Portal Consent Signed
                      </Checkbox>
                    )}
                    {selectedSession.client.pwd && !selectedSession.client.pwd_card && (
                      <Checkbox
                        isChecked={documentStatus.pwd_card}
                        onChange={(e) => setDocumentStatus(prev => ({
                          ...prev,
                          pwd_card: e.target.checked
                        }))}
                      >
                        PWD Card Submitted
                      </Checkbox>
                    )}
                  </VStack>
                </FormControl>
              )}

              {attendanceForm.status === 'absent' && selectedSession && (
              <>
              <FormControl>
                <FormLabel>Amount To Collect</FormLabel>
                <NumberInput
                    value={graceCancellation || waivedLateCancellation ? 0 : 950}
                    min={0}
                    isDisabled={true}
                >
                    <NumberInputField />
                </NumberInput>
              </FormControl>
              <FormControl>
                <Checkbox
                  isChecked={graceCancellation}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    if (checked) {
                      setPaidNoShowFee(false);
                      setWaivedLateCancellation(false);
                    }
                    setGraceCancellation(checked);
                  }}
                  isDisabled={
                    (!graceCancellation &&
                      graceCancellationUsedCount >= GRACE_CANCELLATIONS_PER_YEAR) ||
                    waivedLateCancellation
                  }
                  colorScheme="teal"
                >
                  Grace Cancellation (waive ₱950 fee)
                </Checkbox>
                <Text fontSize="xs" color="gray.500" mt={1} pl={6}>
                  {getGraceCancellationHelperText(graceCancellationUsedCount, graceCancellation)}
                  . Makeup requirement still applies.
                </Text>
              </FormControl>
              <FormControl>
                <Checkbox
                  isChecked={waivedLateCancellation}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    if (checked) {
                      setPaidNoShowFee(false);
                      setGraceCancellation(false);
                    }
                    setWaivedLateCancellation(checked);
                  }}
                  isDisabled={graceCancellation}
                  colorScheme="cyan"
                >
                  Waive Late Cancellation (special cases, e.g. floods)
                </Checkbox>
                <Text fontSize="xs" color="gray.500" mt={1} pl={6}>
                  Waives the ₱950 fee without using a grace cancellation. Makeup requirement still applies.
                </Text>
              </FormControl>
              </>
              )}
              <FormControl isRequired={attendanceForm.status === 'cancelled' || attendanceForm.status === 'absent'}>
                <FormLabel>
                  {attendanceForm.status === 'cancelled' ? 'Reason for Cancellation & Time Messaged' :
                   attendanceForm.status === 'absent' ? 'Reason for Late Cancellation & Time Messaged' :
                   'Session Notes'}
                </FormLabel>
                <Textarea
                  value={attendanceForm.notes}
                  onChange={(e) => setAttendanceForm({
                    ...attendanceForm,
                    notes: e.target.value
                  })}
                  placeholder={
                    attendanceForm.status === 'cancelled' ? 'Enter reason for cancellation and time messaged' :
                    attendanceForm.status === 'absent' ? 'Enter reason for late cancellation and time messaged' :
                    'e.g., Promo Session 1/10'
                  }
                />
              </FormControl>
              
              {(attendanceForm.status === 'cancelled' || attendanceForm.status === 'absent') && (
                <VStack spacing={4} align="stretch" width="100%">
                  {/* For makeup sessions that are cancelled - show special message and only no-show fee option */}
                  {isSupposedMakeupSession(selectedSession) ? (
                    <>
                      <Box p={3} bg="red.50" borderRadius="md" borderWidth="1px" borderColor="red.200">
                        <Text fontSize="sm" color="red.700" fontWeight="medium">
                          This is a supposed makeup session being cancelled or late-cancelled.
                        </Text>
                        <Text fontSize="sm" color="red.600" mt={1}>
                          You can reschedule it to another date (this waives the ₱950 no-show fee), charge the no-show fee, or mark it as cancelled by the therapist (fee waived; original session stops requiring makeup).
                        </Text>
                        <Text fontSize="xs" color="gray.600" mt={2}>
                          Original cancelled session:{' '}
                          {selectedSession.makeup_for
                            ? new Date(selectedSession.makeup_for).toLocaleDateString('en-US', {
                                weekday: 'long',
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric'
                              })
                            : 'Unknown Date'}
                        </Text>
                      </Box>

                      <FormControl>
                        <Checkbox
                          isChecked={rescheduleMakeup}
                          onChange={(e) => {
                            const on = e.target.checked;
                            setRescheduleMakeup(on);
                            if (on) {
                              setPaidNoShowFee(false);
                              setCancelledMakeupByTherapist(false);
                              setRescheduleTherapistId(selectedSession?.therapist?.id || '');
                              setAttendanceForm((prev) => ({
                                ...prev,
                                noMakeupRequired: false,
                              }));
                            } else {
                              setSelectedMakeupSlot(null);
                              setRescheduleTherapistId('');
                            }
                          }}
                          colorScheme="green"
                        >
                          Reschedule to another date (waive no-show fee)
                        </Checkbox>
                        <Text fontSize="xs" color="gray.500" mt={1} pl={6}>
                          The ₱950 no-show fee is waived. The original cancelled session still requires makeup on the new date.
                        </Text>
                      </FormControl>

                      {!rescheduleMakeup && (
                        <>
                          <FormControl>
                            <Checkbox
                              isChecked={cancelledMakeupByTherapist}
                              onChange={(e) => {
                                const on = e.target.checked;
                                setCancelledMakeupByTherapist(on);
                                if (on) {
                                  setPaidNoShowFee(false);
                                  setRescheduleMakeup(false);
                                  setSelectedMakeupSlot(null);
                                }
                              }}
                              colorScheme="blue"
                            >
                              Cancelled by therapist (waive no-show fee; original session stops requiring makeup)
                            </Checkbox>
                            <Text fontSize="xs" color="gray.500" mt={1} pl={6}>
                              Only check if you—the therapist/office—are cancelling this supposed makeup slot, not because
                              the client no-showed.
                            </Text>
                          </FormControl>

                          {!cancelledMakeupByTherapist && (
                            <FormControl>
                              <Checkbox
                                isChecked={paidNoShowFee}
                                onChange={(e) => {
                                  setPaidNoShowFee(e.target.checked);
                                  if (e.target.checked) {
                                    setRescheduleMakeup(false);
                                    setSelectedMakeupSlot(null);
                                  }
                                }}
                                colorScheme="orange"
                              >
                                Paid No-Show Fee (₱950)
                              </Checkbox>
                              <Text fontSize="xs" color="gray.500" mt={1} pl={6}>
                                Check this once the client has paid the no-show fee
                              </Text>
                            </FormControl>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <FormControl>
                        <Checkbox
                          isChecked={attendanceForm.noMakeupRequired}
                          onChange={(e) => {
                            setAttendanceForm({
                              ...attendanceForm,
                              noMakeupRequired: e.target.checked
                            });
                            if (e.target.checked) {
                              setSelectedMakeupSlot(null);
                              setPaidNoShowFee(false);
                            }
                          }}
                          isDisabled={paidNoShowFee}
                        >
                          No makeup session required
                        </Checkbox>
                      </FormControl>

                      {/* Paid No Show Fee Option */}
                      {!attendanceForm.noMakeupRequired && (
                        <FormControl>
                          <Checkbox
                            isChecked={paidNoShowFee}
                            onChange={(e) => {
                              setPaidNoShowFee(e.target.checked);
                              if (e.target.checked) {
                                setSelectedMakeupSlot(null);
                                setCancelledMakeupByTherapist(false);
                              }
                            }}
                            colorScheme="orange"
                          >
                            Paid No-Show Fee (₱950) - bypass makeup session
                          </Checkbox>
                          <Text fontSize="xs" color="gray.500" mt={1} pl={6}>
                            Select this if the client opts to pay the no-show fee instead of scheduling a makeup session
                          </Text>
                        </FormControl>
                      )}
                    </>
                  )}

                  {/* Makeup session slot selection — regular cancellations or makeup reschedule */}
                  {((!selectedSession?.is_makeup &&
                    !attendanceForm.noMakeupRequired &&
                    !paidNoShowFee) ||
                    (selectedSession?.is_makeup &&
                      rescheduleMakeup &&
                      !cancelledMakeupByTherapist &&
                      !paidNoShowFee)) && (
                    <FormControl>
                      <FormLabel>
                        {selectedSession?.is_makeup ? 'Reschedule Makeup Session' : 'Schedule Makeup Session'}
                      </FormLabel>
                      {selectedSession?.is_makeup && rescheduleMakeup && (
                        <FormControl mb={3}>
                          <FormLabel fontSize="sm">Therapist</FormLabel>
                          <Select
                            value={rescheduleTherapistId || selectedSession.therapist?.id || ''}
                            onChange={(e) => {
                              setRescheduleTherapistId(e.target.value);
                              setSelectedMakeupSlot(null);
                            }}
                          >
                            {rescheduleTherapistOptions.map((therapist) => (
                              <option key={therapist.id} value={therapist.id}>
                                {therapist.name}
                                {therapist.id === selectedSession.therapist?.id ? ' (current)' : ''}
                              </option>
                            ))}
                          </Select>
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            Other {selectedSession.therapist?.specialization || 'same-type'} therapists can cover this makeup. Their open slots are listed below.
                          </Text>
                        </FormControl>
                      )}
                      {!selectedSession?.is_makeup &&
                        selectedSession?.attendance?.makeup_scheduled &&
                        existingLinkedMakeup &&
                        !selectedMakeupSlot && (
                        <Badge colorScheme="blue" mb={2} p={2} whiteSpace="normal">
                          Makeup already scheduled for{' '}
                          {new Date(existingLinkedMakeup.date).toLocaleDateString('en-US', {
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric',
                          })}
                          {existingLinkedMakeup.time ? ` at ${formatTime(existingLinkedMakeup.time)}` : ''}
                          . This link will be kept when you save.
                        </Badge>
                      )}
                      {fetchingMakeupSlots ? (
                        <HStack py={2}>
                          <Spinner size="sm" />
                          <Text fontSize="sm" color="gray.500">Loading available slots...</Text>
                        </HStack>
                      ) : availableMakeupSlots.length > 0 ? (
                        <Select
                          value={selectedMakeupSlot ? `${selectedMakeupSlot.date}_${selectedMakeupSlot.time_slot}` : ''}
                          onChange={(e) => {
                            if (e.target.value) {
                              const [date, time] = e.target.value.split('_');
                              const slot = availableMakeupSlots.find(
                                s => s.date === date && s.time_slot === time
                              );
                              setSelectedMakeupSlot(slot);
                            } else {
                              setSelectedMakeupSlot(null);
                            }
                          }}
                          placeholder="Select makeup session slot"
                        >
                          {availableMakeupSlots.map((slot) => {
                            const slotDate = new Date(slot.date);
                            const formattedDate = slotDate.toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric'
                            });
                            const hour = parseInt(slot.time_slot.split(':')[0]);
                            const formattedTime = `${hour > 12 ? hour - 12 : hour}:00 ${hour >= 12 ? 'PM' : 'AM'}`;
                            return (
                              <option key={`${slot.date}_${slot.time_slot}`} value={`${slot.date}_${slot.time_slot}`}>
                                {formattedDate} at {formattedTime}
                              </option>
                            );
                          })}
                        </Select>
                      ) : (
                        <Box p={3} bg="yellow.50" borderRadius="md" borderWidth="1px" borderColor="yellow.200">
                          <Text fontSize="sm" color="yellow.700">
                            No available makeup slots found for the next 14 days. 
                            The therapist needs to open slots in their Makeup Availability tab.
                          </Text>
                        </Box>
                      )}
                      {selectedMakeupSlot && (
                        <Badge mt={2} colorScheme="green" fontSize="sm" p={2}>
                          {selectedSession?.is_makeup ? 'Rescheduled to' : 'Makeup scheduled for:'}{' '}
                          {new Date(selectedMakeupSlot.date).toLocaleDateString('en-US', {
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric'
                          })} at {(() => {
                            const hour = parseInt(selectedMakeupSlot.time_slot.split(':')[0]);
                            return `${hour > 12 ? hour - 12 : hour}:00 ${hour >= 12 ? 'PM' : 'AM'}`;
                          })()}
                        </Badge>
                      )}
                    </FormControl>
                  )}
                </VStack>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button 
              colorScheme="blue" 
              mr={3} 
              onClick={handleAttendanceSubmit}
              isLoading={isSubmittingAttendance}
              loadingText="Saving..."
              isDisabled={
                isSubmittingAttendance ||
                // Require notes for cancellations
                ((attendanceForm.status === 'cancelled' || attendanceForm.status === 'absent') && 
                !attendanceForm.notes?.trim()) ||
                // Require slot when rescheduling a cancelled makeup
                (selectedSession?.is_makeup &&
                  rescheduleMakeup &&
                  (attendanceForm.status === 'cancelled' || attendanceForm.status === 'absent') &&
                  !selectedMakeupSlot)
              }
            >
              Save Attendance
            </Button>
            <Button variant="ghost" onClick={() => setIsAttendanceModalOpen(false)}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
  
      {/* Notes Modal */}
      <Modal 
        isOpen={isNotesModalOpen} 
        onClose={() => setIsNotesModalOpen(false)}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Edit Client Notes
            {selectedSession && ` - ${selectedSession.client.name}`}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>Client Notes</FormLabel>
              <Textarea
                value={sessionNotes}
                onChange={(e) => setSessionNotes(e.target.value)}
                placeholder="Add client notes"
                rows={6}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={handleNotesUpdate}>
              Save Notes
            </Button>
            <Button variant="ghost" onClick={() => setIsNotesModalOpen(false)}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

        {/* One-Time Session Modal */}
        <Modal
        isOpen={isOneTimeModalOpen}
        onClose={() => setIsOneTimeModalOpen(false)}
        size="lg"
        >
        <ModalOverlay />
        <ModalContent>
            <ModalHeader>Add One-Time Session</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
            <VStack spacing={4}>
                <FormControl isRequired>
                <FormLabel>Therapist</FormLabel>
                <Select
                    value={oneTimeSessionForm.therapist_id}
                    onChange={(e) => setOneTimeSessionForm(prev => ({
                    ...prev,
                    therapist_id: e.target.value,
                    pending_makeup_id: '' // Reset pending makeup when therapist changes
                    }))}
                    placeholder="Select therapist"
                >
                    {availableTherapists
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(therapist => (
                      <option key={therapist.id} value={therapist.id}>
                          {therapist.name} - {therapist.specialization}
                      </option>
                    ))}
                </Select>
                </FormControl>

                <FormControl isRequired>
                <FormLabel>Client</FormLabel>
                <Select
                    value={oneTimeSessionForm.client_id}
                    onChange={(e) => setOneTimeSessionForm(prev => ({
                    ...prev,
                    client_id: e.target.value,
                    pending_makeup_id: '' // Reset pending makeup when client changes
                    }))}
                    placeholder="Select client"
                >
                    {availableClients
                      .sort(compareNamesByFamilyName)
                      .map(client => (
                      <option key={client.id} value={client.id}>
                          {formatNameLastCommaFirst(client.name)}
                          {client.nickname ? ` ${client.nickname}` : ''}
                      </option>
                    ))}
                </Select>
                </FormControl>

                {/* Pending Makeup Sessions Dropdown */}
                {oneTimeSessionForm.therapist_id && oneTimeSessionForm.client_id && (
                  <FormControl>
                    <FormLabel>
                      Assign to Pending Makeup Session
                      <Text as="span" fontSize="xs" color="gray.500" ml={2}>(Optional)</Text>
                    </FormLabel>
                    <Text fontSize="xs" color="gray.500" mb={2}>
                      Shows all unscheduled makeups for this client matching the selected therapist&apos;s therapy type (
                      {availableTherapists.find((t) => t.id === oneTimeSessionForm.therapist_id)?.specialization || '—'}).
                    </Text>
                    {loadingClientMakeups ? (
                      <HStack py={2}>
                        <Spinner size="sm" />
                        <Text fontSize="sm" color="gray.500">Loading pending makeups...</Text>
                      </HStack>
                    ) : clientPendingMakeups.length > 0 ? (
                      <>
                        <Select
                          value={oneTimeSessionForm.pending_makeup_id}
                          onChange={(e) => setOneTimeSessionForm(prev => ({
                            ...prev,
                            pending_makeup_id: e.target.value
                          }))}
                          placeholder="Select pending makeup (or leave blank for regular session)"
                        >
                          {clientPendingMakeups.map(makeup => {
                            const cancelledDate = new Date(makeup.attendance_date);
                            const formattedDate = cancelledDate.toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            });
                            const formattedTime = makeup.session?.time 
                              ? (() => {
                                  const hour = parseInt(makeup.session.time.split(':')[0]);
                                  return `${hour > 12 ? hour - 12 : hour}:00 ${hour >= 12 ? 'PM' : 'AM'}`;
                                })()
                              : '';
                            const therapistLabel = makeup.therapist?.name
                              ? ` · ${makeup.therapist.name}`
                              : '';
                            return (
                              <option key={makeup.id} value={makeup.id}>
                                {formattedDate} {formattedTime && `at ${formattedTime}`} - {makeup.status === 'absent' ? 'Late Cancellation' : 'Cancelled'}{therapistLabel}
                              </option>
                            );
                          })}
                        </Select>
                        {oneTimeSessionForm.pending_makeup_id && (
                          <Badge mt={2} colorScheme="purple" fontSize="sm" p={2}>
                            This will be scheduled as a makeup session
                          </Badge>
                        )}
                      </>
                    ) : (
                      <Box p={3} bg="gray.50" borderRadius="md" borderWidth="1px" borderColor="gray.200">
                        <Text fontSize="sm" color="gray.500">
                          No pending {availableTherapists.find((t) => t.id === oneTimeSessionForm.therapist_id)?.specialization || 'therapy'} makeups found for this client
                        </Text>
                      </Box>
                    )}
                  </FormControl>
                )}

                <FormControl isRequired>
                <FormLabel>Time</FormLabel>
                <Select
                    value={oneTimeSessionForm.time}
                    onChange={(e) => setOneTimeSessionForm(prev => ({
                    ...prev,
                    time: e.target.value
                    }))}
                    placeholder="Select time"
                >
                    {TIME_SLOTS.map(time => (
                    <option key={time} value={time}>
                        {formatTime(time)}
                    </option>
                    ))}
                </Select>
                </FormControl>

                <FormControl>
                <FormLabel>Notes</FormLabel>
                <Textarea
                    value={oneTimeSessionForm.notes}
                    onChange={(e) => setOneTimeSessionForm(prev => ({
                    ...prev,
                    notes: e.target.value
                    }))}
                    placeholder="Add any notes about this one-time session"
                />
                </FormControl>
            </VStack>
            </ModalBody>
            <ModalFooter>
            <Button
                colorScheme="blue"
                mr={3}
                onClick={handleAddOneTimeSession}
                isDisabled={!oneTimeSessionForm.therapist_id || !oneTimeSessionForm.client_id || !oneTimeSessionForm.time}
            >
                Add Session
            </Button>
            <Button variant="ghost" onClick={() => setIsOneTimeModalOpen(false)}>
                Cancel
            </Button>
            </ModalFooter>
        </ModalContent>
        </Modal>
        {/* Add this near your other modals */}
        <VATCalculatorModal 
            isOpen={isVATCalculatorOpen}
            onClose={() => setIsVATCalculatorOpen(false)}
        />
    </Box>
  );
};

export default DailySchedule;